import { leerEntrada, guardarEntrada, borrarEntrada, hayEntrada } from './entradaGuardada.js';
import { useEffect, useState, useCallback } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus, InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest, tokenRequest } from './auth.js';
import { IS_DEMO } from './demoMode.js';
import { AutenticadorCodigoScreen, AutenticadorAltaScreen } from './components/AutenticadorPanel.jsx';
import { reportFrontendError } from './sentry.js';
import { demoRespond } from './demoApi.js';
// Solo el emisor (`toast`), que no depende de React ni de este archivo — no
// hay import circular: Toast.jsx solo importa react y lucide-react.
import { toast } from './components/Toast.jsx';
import {
  lookupInvitation,
  acceptInvitation,
  registerAccount,
  getLegalStatus,
  acceptLegal,
  listBillingProducts,
  createBillingCheckout,
} from './api.js';
import { QuoteLandingPage } from './components/QuoteLandingPage.jsx';
import { DocPortalLandingPage } from './components/DocPortalLandingPage.jsx';
import { DocSharePage } from './components/DocSharePage.jsx';
import { InvoicePortalLandingPage } from './components/InvoicePortalLandingPage.jsx';
import { SignaturePortalPage } from './components/SignaturePortalPage.jsx';
import { OAuthCallbackPage } from './components/MyEmailView.jsx';
import { PublicFormPage } from './components/PublicFormPage.jsx';
import { ClientPortalPage } from './components/ClientPortalPage.jsx';
import { PublicResourcePortal } from './components/PublicResourcePortal.jsx';
import { RenewalReviewPage } from './components/RenewalReviewPage.jsx';
import { EncuestaPublicPage } from './components/EncuestaPublicPage.jsx';
import { BookingPublicPage } from './components/BookingPublicPage.jsx';
import { AgentCardPage } from './components/AgentCardPage.jsx';
import { BookingManagePage } from './components/BookingManagePage.jsx';
import { IntakePublicPage } from './components/IntakePublicPage.jsx';
import { ApplicationPublicPage } from './components/ApplicationPublicPage.jsx';
import {
  ESPERAS_DE_REINTENTO, marcarReintentando, marcarServidorVivo,
} from './estadoDelServidor.js';
import { limiteDeTiempoPara, relojDeIntento } from './limiteDeTiempo.js';

function isOAuthCallbackPath() {
  if (typeof window === 'undefined') return false;
  return window.location.pathname === '/oauth-callback';
}

function getPublicFormSlugFromPath() {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/^\/form\/([a-z0-9-]{1,80})$/i);
  return m ? m[1] : null;
}

function getPortalTokenFromPath() {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/^\/cliente\/([A-Za-z0-9_-]{20,})$/);
  return m ? m[1] : null;
}

// Public renewal-review landing — /revision/:token. No auth (token-protected).
function getRenewalReviewTokenFromPath() {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/^\/revision\/([A-Za-z0-9_-]{20,})$/);
  return m ? m[1] : null;
}

// Encuesta al cliente — /encuesta/:token. Sin sesión, el token es la llave.
function getEncuestaTokenFromPath() {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/^\/encuesta\/([A-Za-z0-9_-]{20,80})$/);
  return m ? m[1] : null;
}

// Public booking landing — /book/:slug. No auth required (slug-protected).
function getBookingSlugFromPath() {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/^\/book\/([a-z0-9-]{1,80})$/i);
  return m ? m[1] : null;
}

// Tarjeta digital publica — /t/:slug. Sin auth: el slug es la llave.
// Misma expresion de slug que el agendamiento, para que no haya dos reglas
// distintas de lo que es una direccion valida.
function getCardSlugFromPath() {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/^\/t\/([a-z0-9-]{3,60})$/i);
  return m ? m[1].toLowerCase() : null;
}

// Public booking management — /book/manage/:token. No auth (token-protected).
function getBookingManageTokenFromPath() {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/^\/book\/manage\/([A-Za-z0-9-]{10,})$/);
  return m ? m[1] : null;
}

// If the URL is /join/<token>, return the token. Otherwise null.
function getJoinTokenFromPath() {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/^\/join\/([A-Za-z0-9_-]{20,})$/);
  if (m) return m[1];
  // Survives MSAL redirect roundtrip via sessionStorage
  return sessionStorage.getItem('deskte:pendingInviteToken') || null;
}

// Public quote landing — /quote/:token. No auth required.
function getQuoteTokenFromPath() {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/^\/quote\/([A-Za-z0-9_-]{20,})$/);
  return m ? m[1] : null;
}

// Public enrollment form — /intake/:token. No auth (token-protected).
// Shown after a client accepts a plan; submitting creates the client.
function getIntakeTokenFromPath() {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/^\/intake\/([A-Za-z0-9_-]{20,})$/);
  return m ? m[1] : null;
}

// Public application form — /application/:token. No auth (token-protected).
function getApplicationTokenFromPath() {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/^\/application\/([A-Za-z0-9_-]{20,})$/);
  return m ? m[1] : null;
}

// Public document upload portal — /upload/:token. No auth required.
function getUploadTokenFromPath() {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/^\/upload\/([A-Za-z0-9_-]{20,})$/);
  return m ? m[1] : null;
}

// Public OUTBOUND document share — /share/:token. No auth required. This is the
// link a carrier receives when the agency sends them a client's documents.
function getShareTokenFromPath() {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/^\/share\/([A-Za-z0-9_-]{20,})$/);
  return m ? m[1] : null;
}

// Public invoice landing — /invoice/:token. No auth required.
function getInvoiceTokenFromPath() {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/^\/invoice\/([A-Za-z0-9_-]{20,})$/);
  return m ? m[1] : null;
}

// Public signature portal — /sign/:token. No auth required.
function getSignTokenFromPath() {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/^\/sign\/([A-Za-z0-9_-]{20,})$/);
  return m ? m[1] : null;
}

// Public client resource portal — /resource-portal/:token. No auth required.
// Token form is "portal_<clientId>_<rand>" (underscores), so allow them.
function getResourcePortalTokenFromPath() {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/^\/resource-portal\/([A-Za-z0-9_-]{8,})$/);
  return m ? m[1] : null;
}

// Public single-resource link — /r/:token. No auth. The page does nothing but
// hand off to the API, which verifies the HMAC and redirects to a short-lived
// signed file URL. It lives on the app domain (not the API domain) so what the
// agent pastes into WhatsApp looks like Deskte. HMAC tokens are base64url with
// exactly one dot separating payload and signature.
function getResourceLinkTokenFromPath() {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/^\/r\/([A-Za-z0-9_-]+\.[A-Za-z0-9_-]+)$/);
  return m ? m[1] : null;
}

// Singleton token getter — exposed to the app so any fetch can use it.
// Returns a fresh access token (silent refresh under the hood).
let _instance = null;
export async function getAccessToken() {
  if (!_instance) throw new Error('MSAL instance not ready');
  const account = _instance.getActiveAccount() || _instance.getAllAccounts()[0];
  if (!account) throw new Error('Not signed in');
  try {
    const r = await _instance.acquireTokenSilent({ ...tokenRequest, account });
    return r.accessToken;
  } catch (e) {
    // Silent renewal failed — recover with a full-page redirect re-auth instead
    // of bubbling the error up to the "No pudimos cargar tu perfil" screen.
    //  - InteractionRequiredAuthError: the IdP genuinely needs the user
    //    (expired session, consent, MFA).
    //  - errorCode 'timed_out': the hidden silent-renewal iframe never
    //    completed. Historically the app's own anti-framing headers blocked it
    //    (fixed 2026-06-12: frame-ancestors 'self' + X-Frame-Options SAMEORIGIN
    //    in staticwebapp.config.json); still possible when a browser blocks the
    //    third-party cookies the iframe needs to see the Entra session.
    const code = e?.errorCode || '';
    if (e instanceof InteractionRequiredAuthError || code === 'timed_out') {
      await _instance.acquireTokenRedirect({ ...tokenRequest, account });
    }
    throw e;
  }
}

const API_BASE = import.meta.env.VITE_API_URL || '';

// localStorage key for the single-active-session token issued by /auth/me.
// Stored in localStorage (not sessionStorage) so multiple tabs in the same
// browser share one session — opening tab #2 of the same device should NOT
// kick tab #1 out. Logging in on a DIFFERENT device rotates the id server-
// side, which is what triggers the eviction.
//
// The id is stored PER MSAL ACCOUNT — `deskte:sessionId:<accountId>`. This
// constant is the PREFIX, and on its own it is the legacy pre-2026-08-17 key:
// still read once to migrate, never written again.
//
// A shared agency computer keeps several accounts cached in MSAL at once. With
// one shared key, the next person to sign in echoed the PREVIOUS user's
// X-Session-Id, and the backend answered 401 SESSION_REPLACED: "you signed in
// on another device" when they hadn't ("No pudimos cargar tu perfil", Lissette
// 2026-08-17). The account-switch detection below was the only thing standing
// between users, and it silently does nothing whenever MSAL reports a stale
// active account. Scoping the key removes the guesswork: an id belonging to
// another account simply isn't read.
const SESSION_ID_KEY = 'deskte:sessionId';

// Resolved EXACTLY like getAccessToken() picks the account it mints the bearer
// token from — that is what keeps the stored id and the identity in the token
// pointing at the same person, whichever account MSAL considers active.
function activeAccountId() {
  try {
    const acct = (_instance && (_instance.getActiveAccount() || _instance.getAllAccounts()[0])) || null;
    return acct?.localAccountId || acct?.homeAccountId || null;
  } catch (_) { return null; }
}

// Copia en MEMORIA del id que el servidor emitió para esta pestaña.
//
// Sin ella el candado se muerde la cola. `getSessionId()`/`setSessionId()`
// resuelven la cuenta de MSAL en cada llamada, y si MSAL tropieza justo en ese
// instante —no ha terminado de inicializar, `endpoints_resolution_error`, la
// cuenta activa viene nula— pasan dos cosas seguidas: se manda /auth/me SIN
// cabecera (el servidor lo lee como entrada nueva y ROTA el id) y el id nuevo
// NO se llega a guardar, porque tampoco hay cuenta bajo la que guardarlo. El
// navegador se queda con el id viejo y, al latido siguiente (10 s), el servidor
// contesta 401 SESSION_REPLACED: "iniciaste sesión en otro dispositivo" — sin
// que exista ningún otro dispositivo (Lissette 2026-08-19).
//
// La memoria manda al leer: es lo que el servidor acaba de decir que vale para
// esta pestaña. Es por pestaña, así que no puede cruzarse entre cuentas, y el
// cambio de cuenta la limpia vía setSessionId(null).
let _sessionIdMem = null;

// APODO DE ESTE NAVEGADOR.
//
// "Chrome en Windows" no distingue SU computadora de OTRA computadora con
// Chrome, y esa es justo la pregunta que hay que contestar cuando alguien dice
// "yo no abrí nada" (Lissette 2026-08-19, tres capturas seguidas). Cuatro
// caracteres al azar, generados aquí y guardados en este navegador, y de
// pronto "Chrome en Windows · A3F2" se puede comparar con "Chrome en Windows ·
// 7B1C" — la misma máquina o no.
//
// Es un apodo, no una huella: se inventa al azar, no dice nada de la persona
// ni del equipo, y si el navegador lo borra simplemente se genera otro. No
// sirve para identificar a nadie, solo para responder "¿fui yo?".
const DEVICE_TAG_KEY = 'deskte:deviceTag';
export function getDeviceTag() {
  try {
    let t = localStorage.getItem(DEVICE_TAG_KEY);
    if (!t) {
      t = Math.floor(Math.random() * 0xffff).toString(16).toUpperCase().padStart(4, '0');
      localStorage.setItem(DEVICE_TAG_KEY, t);
    }
    return t;
  } catch (_) { return null; }
}

function sessionKeyFor(accountId) {
  return accountId ? `${SESSION_ID_KEY}:${accountId}` : null;
}

// El único id guardado en este navegador, si hay exactamente uno. Con dos o
// más cuentas cacheadas devuelve null: adivinar es lo que rompía antes.
function soleStoredSessionId() {
  try {
    const found = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(`${SESSION_ID_KEY}:`)) found.push(k);
    }
    return found.length === 1 ? localStorage.getItem(found[0]) : null;
  } catch (_) { return null; }
}

export function getSessionId() {
  // Lo que el servidor emitió para esta pestaña gana a lo guardado: ver
  // _sessionIdMem. Si no hay cuenta resoluble, esto es lo único que evita
  // presentar un id que el servidor ya rotó.
  if (_sessionIdMem) return _sessionIdMem;
  const acctId = activeAccountId();
  // Sin cuenta resoluble y sin memoria —una pestaña recién abierta mientras
  // MSAL todavía arranca— antes se mandaba sin cabecera, y eso hace que el
  // servidor ROTE el id: la propia pestaña sobrevive (minar nunca expulsa a
  // quien pide), pero las OTRAS pestañas del mismo navegador se quedan con el
  // id viejo y se llevan la expulsión. Si en este navegador hay UNA sola
  // sesión guardada, no hay ambigüedad posible: es de esta persona y de este
  // equipo, así que se presenta. Con varias cuentas en la misma máquina no se
  // adivina — se sigue mandando sin cabecera, que es el caso que el reparto
  // por cuenta vino a arreglar.
  if (!acctId) return soleStoredSessionId();
  try {
    const scoped = localStorage.getItem(sessionKeyFor(acctId));
    if (scoped) return scoped;
    // One-time migration off the shared key. LAST_ACCOUNT_KEY records which
    // account that value was issued to, so it is only adopted for that account
    // — never handed to whoever signs in next.
    const legacy = localStorage.getItem(SESSION_ID_KEY);
    if (legacy && localStorage.getItem(LAST_ACCOUNT_KEY) === acctId) {
      localStorage.setItem(sessionKeyFor(acctId), legacy);
      localStorage.removeItem(SESSION_ID_KEY);
      return legacy;
    }
    return null;
  } catch (_) { return null; }
}

export function setSessionId(id) {
  // La memoria se actualiza SIEMPRE, incluso si no hay cuenta bajo la que
  // guardar en disco. Un id que no se puede guardar es justo el que no se
  // puede olvidar.
  _sessionIdMem = id || null;
  try {
    // Drop the legacy key on any write: from here on the scoped one is the
    // only source of truth, and a leftover value must not resurface.
    localStorage.removeItem(SESSION_ID_KEY);
    const key = sessionKeyFor(activeAccountId());
    if (!key) return;
    if (id) localStorage.setItem(key, id);
    else localStorage.removeItem(key);
  } catch (_) { /* private mode / quota — non-fatal */ }
}

// Two tabs booting at the same moment (restored window, double-clicked
// shortcut) each sent a header-less /auth/me and each minted a NEW id
// server-side. Whichever response landed last in localStorage was not
// necessarily the id the server kept, so the browser went on echoing an id
// that had already been rotated — and evicted ITSELF on the next 10 s
// heartbeat, with no second device involved. Serializing the handshake means
// the second tab reads the id the first one stored and just reuses it.
// EL SALUDO DE ARRANQUE, Y POR QUÉ TODO LO DEMÁS TIENE QUE ESPERARLO.
//
// Al arrancar, /auth/me puede EMITIR una sesión nueva. Mientras esa llamada
// viaja, la aplicación ya está disparando las suyas —actividad, avisos,
// presencia, el latido— y todas leen el id ANTES de que llegue el nuevo. O sea
// que salen con el id viejo, el servidor ya tiene el nuevo, y contesta 401:
// "iniciaste sesión en otro dispositivo".
//
// No hay otro dispositivo. Es la misma pestaña expulsándose a sí misma al
// refrescar (Lissette 2026-08-19, con captura: "estoy sola, refresqué y me
// salió", y el aviso decía "Chrome en Windows" a la hora exacta del refresco).
// En Sentry se ve igual de claro: cinco rutas distintas fallando en el MISMO
// segundo, que es la ráfaga del arranque, no un segundo aparato.
//
// Por eso ninguna llamada sale hasta que el saludo termina. La única excepción
// es el propio saludo, que si esperara se esperaría a sí mismo.
let _resolverSaludo = null;
let _saludoListo = null;
function abrirSaludo() {
  if (_saludoListo) return;
  _saludoListo = new Promise((res) => { _resolverSaludo = res; });
}
function cerrarSaludo() {
  if (_resolverSaludo) { _resolverSaludo(); _resolverSaludo = null; }
}
async function esperarSaludo(path) {
  // El saludo no se espera a sí mismo. El resto sí, con tope: si algo sale mal
  // en el arranque, la aplicación no se queda muda para siempre.
  if (!_saludoListo || String(path || '').startsWith('/auth/me')) return;
  await Promise.race([_saludoListo, new Promise((r) => setTimeout(r, 8000))]);
}

async function withBootHandshakeLock(run) {
  const locks = (typeof navigator !== 'undefined' && navigator.locks) || null;
  if (!locks || typeof locks.request !== 'function') return run();
  let started = false;
  const guarded = () => { started = true; return run(); };
  // Never let a hung tab strand this one on the splash screen: stop waiting
  // after 8 s and do the handshake unlocked (the pre-lock behaviour).
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), 8000) : null;
  try {
    return await locks.request('deskte:auth-handshake', ctrl ? { signal: ctrl.signal } : {}, guarded);
  } catch (e) {
    if (started) throw e;   // the handshake itself failed — the caller owns it
    return run();           // never got the lock (aborted / unsupported)
  } finally {
    if (timer) clearTimeout(timer);
  }
}

// Tracks whether we already forced a logout from a SESSION_TIMEOUT /
// SESSION_REPLACED response, so concurrent in-flight requests don't each
// trigger their own redirect.
let _sessionTimedOut = false;

// Handler the AuthGate registers so we can render a proper React modal
// for SESSION_REPLACED instead of a native alert(). Falls back to the
// previous alert+logoutRedirect behavior if AuthGate hasn't mounted yet.
let _sessionReplacedHandler = null;
// Con qué se quedó la sesión: { at, device } tal y como lo manda el 401. Se
// guarda aparte del estado de React porque quien lo recibe es apiFetch, que
// vive fuera del árbol (Lissette 2026-08-19: el asiento tenía que poder ver si
// era su propio teléfono o alguien más con su usuario).
let _sessionReplacedBy = null;
export function getSessionReplacedBy() { return _sessionReplacedBy; }
export function setSessionReplacedHandler(fn) {
  _sessionReplacedHandler = fn;
}

// El servidor contesta 401 MFA_REQUIRED cuando la cuenta tiene el autenticador
// activo y ESTA sesión aún no dio el código (auditoría de seguridad
// 2026-09-27). No es una expulsión: no se cierra nada, AuthGate solo tapa el
// CRM con la pantalla del código hasta que se escriba.
let _mfaRequiredHandler = null;
// Igual, pero cuando el autenticador es obligatorio y la persona aún no lo
// configuró (401 MFA_ENROLL_REQUIRED): AuthGate enseña la pantalla del QR.
let _mfaEnrollHandler = null;
function setMfaEnrollHandler(fn) {
  _mfaEnrollHandler = fn;
}
function setMfaRequiredHandler(fn) {
  _mfaRequiredHandler = fn;
}

// Platform-owner browse-as-agency. The picker writes the target agency
// id here so apiFetch attaches it to every request. Backend tenant
// middleware swaps tenant.agencyId to this value when the caller is a
// platform owner. Cleared via clearImpersonateAgencyId() when the user
// exits the impersonation.
const IMPERSONATE_KEY = 'deskte:impersonateAgencyId';
const IMPERSONATE_USER_KEY = 'deskte:impersonateUserId';
// The reason the operator typed when starting the browse/view session.
// Backend tenantMiddleware rejects with 400 when the impersonation id
// header is present but this one is missing — HIPAA accounting-of-
// disclosures requires a why, not just a who.
const IMPERSONATE_REASON_KEY = 'deskte:impersonateReason';

// HTTP header values are ISO-8859-1 (Latin-1) only. fetch() throws
// TypeError "String contains non ISO-8859-1 code point" for anything outside
// that range and the request never leaves the browser — which took the whole
// SPA down with "No pudimos cargar tu perfil", because /auth/me itself could
// not be sent.
//
// The reason is free text: typed into the browse-as-agency prompt, or pasted
// from Word/WhatsApp. An em dash, a curly quote or an emoji is enough to brick
// the session until localStorage is cleared. Transliterate the punctuation
// that actually shows up, drop control characters (header injection), and
// discard whatever is still out of range.
//
// Accents are safe and preserved: á/é/ñ/ü all live inside Latin-1.
// Escapes on purpose: these code points are invisible or easily mangled
// when written as literals, and Vite emits pure-ASCII output for this app.
const HEADER_TRANSLITERATIONS = [
  [/[\u2010-\u2015\u2212]/g, '-'],        // hyphen, en/em dash, minus
  [/[\u2018\u2019\u201A\u201B\u2032]/g, "'"],  // curly single quotes, prime
  [/[\u201C\u201D\u201E\u201F\u2033]/g, '"'],  // curly double quotes
  [/\u2026/g, '...'],                        // ellipsis
  [/[\u00A0\u2007\u2009\u200A\u202F\u200B]/g, ' '], // exotic + zero-width spaces
];

export function toHeaderSafe(value) {
  let s = value == null ? '' : String(value);
  for (const [re, to] of HEADER_TRANSLITERATIONS) s = s.replace(re, to);
  s = s.replace(/[\r\n\t]/g, ' ');            // no header injection / folding
  // eslint-disable-next-line no-control-regex
  s = s.replace(/[\u0000-\u001F\u007F]/g, '');  // control characters
  s = s.replace(/[^\u0020-\u00FF]/g, '');      // anything still outside Latin-1
  return s.replace(/\s+/g, ' ').trim();
}

// Todo pasa por entradaGuardada.js: localStorage y, si no cabe, el respaldo de
// la pestaña (Lissette 2026-10-26, "[al abrir: NO estaba]": su localStorage no
// aceptaba la entrada y aquí se tragaba el error). Los set* devuelven dónde
// quedó ('local' | 'pestana') o null si no cupo, para que el botón lo diga en
// vez de recargar a ciegas.
function getImpersonateAgencyId() { return leerEntrada(IMPERSONATE_KEY); }
function getImpersonateUserId() { return leerEntrada(IMPERSONATE_USER_KEY); }
function getImpersonateReason() { return leerEntrada(IMPERSONATE_REASON_KEY); }
export function setImpersonateReason(reason) {
  // Sanitize on the way IN as well as on the way out, so a reason typed with
  // an em dash never even reaches storage.
  const v = toHeaderSafe(reason);
  if (v) return guardarEntrada(IMPERSONATE_REASON_KEY, v.slice(0, 500));
  borrarEntrada(IMPERSONATE_REASON_KEY);
  return null;
}
export function setImpersonateAgencyId(agencyId) {
  if (agencyId) {
    // Agency- and user-impersonation are mutually exclusive.
    borrarEntrada(IMPERSONATE_USER_KEY);
    return guardarEntrada(IMPERSONATE_KEY, agencyId);
  }
  borrarEntrada(IMPERSONATE_KEY);
  borrarEntrada(IMPERSONATE_REASON_KEY);
  return null;
}
export function clearImpersonateAgencyId() {
  setImpersonateAgencyId(null);
}
export function setImpersonateUserId(userId) {
  if (userId) {
    borrarEntrada(IMPERSONATE_KEY);
    return guardarEntrada(IMPERSONATE_USER_KEY, userId);
  }
  borrarEntrada(IMPERSONATE_USER_KEY);
  borrarEntrada(IMPERSONATE_REASON_KEY);
  return null;
}
export function clearImpersonateUserId() {
  setImpersonateUserId(null);
}

// Remembers which MSAL account this browser last bootstrapped, so we can
// detect an account SWITCH (agent → seat, or vice versa) on a SHARED
// computer. On a switch we must NOT carry the previous user's
// single-session id or impersonation headers into the new user's first
// /auth/me: the backend would see a stale X-Session-Id, reject the new
// user with a spurious SESSION_REPLACED, and they'd land on an empty CRM
// ("HAIFFA can't see the clients"). Reproduced 2026-05-18: same PC, login
// as agent → logout → login as seat → seat saw no data.
const LAST_ACCOUNT_KEY = 'deskte:lastAccountId';

// Si una entrada de Platform Admin ("Entrar" / "Ver como") se pierde al
// recargar, se deja dicho POR QUÉ, y Platform Admin lo enseña en un aviso
// (Lissette 2026-10-26: "coloco el motivo pero no me deja entrar", y la
// pantalla volvía a la plataforma sin decir nada). Sin esto no hay forma de
// saber desde fuera qué la borró.
export const ENTRADA_PERDIDA_KEY = 'deskte:entradaPerdida';

/**
 * Una entrada guardada ("Entrar" / "Ver como") que el servidor NO aplicó a esta
 * cuenta —no es la dueña de la plataforma ni la está viendo— no le sirve de
 * nada y se borra, con su motivo. La decide el servidor, no el navegador.
 */
export function limpiarEntradaQueNoEsDeEstaCuenta(u) {
  if (!u || u.role === 'PLATFORM_OWNER' || u.impersonatedByPlatform === true) return false;
  if (!hayEntrada()) return false;
  anotarEntradaPerdida('cuenta-sin-permiso');
  borrarEntrada(IMPERSONATE_KEY);
  borrarEntrada(IMPERSONATE_USER_KEY);
  borrarEntrada(IMPERSONATE_REASON_KEY);
  return true;
}
function anotarEntradaPerdida(motivo) {
  try {
    if (hayEntrada()) localStorage.setItem(ENTRADA_PERDIDA_KEY, motivo);
  } catch (_) {}
}

// LA ENTRADA DE PLATFORM ADMIN YA NO SE BORRA AQUÍ (Lissette 2026-10-26, aviso
// E1: "se borró porque el navegador cambió de cuenta de Microsoft"). En su
// navegador esta comprobación veía un "cambio de cuenta" en CADA recarga, y
// borraba la entrada a la agencia que acababa de pedir: "Entrar" y "Ver como"
// volvían siempre a la plataforma. Borrarla aquí no protegía nada: el servidor
// solo hace caso a esa entrada si quien llama es la dueña de la plataforma
// (tenant.middleware.ts y /auth/me). Ahora se borra DESPUÉS de /auth/me, y solo
// si el servidor dice que esta cuenta no puede usarla (ver
// `limpiarEntradaQueNoEsDeEstaCuenta`). Aquí se sigue limpiando la sesión.
function resetClientAuthStateForAccountSwitch() {
  try { setSessionId(null); } catch (_) {}
  // A previous account may have tripped the one-shot eviction latch; the
  // new account must start un-latched or its first 401 handling is wrong.
  _sessionTimedOut = false;
}

// ── A read that fails for one second must not empty a screen for an hour ──
//
// Lissette, 2026-08-20: she opened Deskte while the backend was mid-restart
// and the "assign to" list came up EMPTY — no error, no spinner, nothing. It
// stayed empty until she reloaded the page.
//
// Nothing was broken. One GET landed in the second the container was cycling.
// The damage came from what happens next: most lists in deskte-full.jsx
// hydrate ONCE behind a `somethingLoaded` latch, and their `.catch` only
// writes to the console. So a single blip leaves the state at its `[]` initial
// value forever, and — worse than the hole — leaves it silent. Deskte looks
// broken when all it needed was to ask again.
//
// Retrying HERE fixes it for every list at once instead of patching twenty
// effects, because this is the one door all of api.js goes through.
// Las esperas y el aviso en pantalla viven en `estadoDelServidor.js`.
//
// Los 7,7 segundos de paciencia originales cubrian un reinicio corto, no un
// DESPLIEGUE: el contenedor nuevo carga las contraseñas de Azure antes de
// empezar a contestar, y eso dura bastante mas. Quien hacia clic en esa
// ventana se quedaba fuera del reintento — aviso rojo y pantalla vacia
// (Lissette 2026-09-01: "se les cuelga por un minuto, esto no abre").
const RETRY_BACKOFF_MS = ESPERAS_DE_REINTENTO;

/**
 * Worth asking again? Only the answers a restarting container produces.
 *
 * NOT 500: that is the application itself failing, and repeating the same
 * request just reproduces the same bug three times. NOT 4xx: 401/403/404 are
 * real answers, not accidents.
 */
function isTransientStatus(status) {
  return status === 502 || status === 503 || status === 504;
}

// One notice per 30s no matter how many calls fail. A page load fires a dozen
// of these at once; without the throttle a backend outage would stack a dozen
// identical red toasts on top of each other.
let _lastOutageNoticeAt = 0;
function noticeServerUnreachable() {
  const now = Date.now();
  if (now - _lastOutageNoticeAt < 30000) return;
  _lastOutageNoticeAt = now;
  try {
    toast.error(
      browserLang() === 'en'
        ? 'Could not reach the server. Some lists may be incomplete — reload the page.'
        : 'No se pudo conectar con el servidor. Puede que algunas listas esten incompletas — recarga la pagina.',
      { duration: 9000 },
    );
  } catch (_) { /* a notice must never be the thing that breaks the call */ }
}

// MODO SOLO LECTURA: la agencia esta suspendida y el servidor rechaza toda
// escritura con 403 READ_ONLY (ver deskte-api/src/middleware/soloLectura...).
//
// Esto tiene que estar AQUI, en el unico sitio por el que pasan todas las
// llamadas, y no en cada pantalla. deskte-full.jsx tiene cientos de sitios que
// guardan algo y cada uno maneja su error a su manera: unos ponen un toast,
// otros se lo tragan y dejan el boton girando. Sin este aviso central, a la
// mitad de los guardados no pasaria nada visible y la persona pensaria que
// Deskte se rompio, no que su cuenta esta en solo lectura.
//
// Uno cada 30 s como mucho, igual que los otros dos: un formulario que guarda
// cinco cosas a la vez no puede sacar cinco avisos identicos.
let _ultimoAvisoDeSoloLectura = 0;
// Una sola recarga por pestaña aunque lleguen diez 403 AGENCY_PAUSED a la vez.
let _recargandoPorPausa = false;

function avisarSoloLectura(mensajeDelServidor) {
  const now = Date.now();
  if (now - _ultimoAvisoDeSoloLectura < 30000) return;
  _ultimoAvisoDeSoloLectura = now;
  try {
    toast.error(
      mensajeDelServidor || (browserLang() === 'en'
        ? 'Your account is in read-only mode: you can view and export, but not create or edit. Activate your plan to get full access back.'
        : 'Tu cuenta esta en modo solo lectura: puedes ver y exportar, pero no crear ni editar. Activa tu plan para recuperar el acceso completo.'),
      { duration: 9000 },
    );
  } catch (_) { /* un aviso nunca puede ser lo que tumbe la llamada */ }
}

// Mismo criterio que el aviso de servidor caido: uno cada 30 s como mucho. Una
// pantalla lanza una docena de llamadas a la vez y si todas se pasan de tiempo
// no puede salir una docena de avisos rojos encima del otro.
let _ultimoAvisoDeTiempo = 0;
function avisarQueTardoDemasiado() {
  const ahora = Date.now();
  if (ahora - _ultimoAvisoDeTiempo < 30000) return;
  _ultimoAvisoDeTiempo = ahora;
  try {
    toast.error(
      browserLang() === 'en'
        ? 'This took too long and was cancelled. Please try again.'
        : 'Esto tardo demasiado y se cancelo. Vuelve a intentarlo.',
      { duration: 9000 },
    );
  } catch (_) { /* un aviso nunca puede ser lo que rompa la llamada */ }
}

// ── CUANTOS INTENTOS COSTO, QUE ES LO QUE EL RELOJ NO DISTINGUE ──────────
//
// Lissette, 2026-09-02: una lectura del cronometro de Pagos del Mes dio
// `servidor 290 ms · viaje 36.812 ms`. Treinta y siete segundos de "viaje" con
// el servidor contestando en tres decimas.
//
// El problema es que ese "viaje" se calcula como (lo que tardo apiFetch) menos
// (lo que dice el servidor), y apiFetch REINTENTA: cuando el backend esta
// reiniciandose contesta 502/503 y aqui se espera 700 ms, 2 s, 5 s, 8 s, 12 s,
// 15 s... hasta 88 segundos en total. Esas esperas caen enteras dentro del
// "viaje" y lo hacen indistinguible de una conexion lenta. Son dos cosas
// completamente distintas -- una es un despliegue en marcha y la otra es la
// red de la persona -- y llevaban al mismo numero.
//
// Con esto el numero deja de ser ambiguo: si costo 1 intento, el tiempo es red
// de verdad; si costo 4, son las esperas del reintento y el servidor estaba
// reiniciandose.
//
// Va en un WeakMap y no como propiedad de la respuesta: `Response` es un
// objeto del navegador y pegarle campos propios es pedir problemas. La entrada
// se va sola cuando la respuesta se recoge.
export const intentosDeLaPeticion = new WeakMap();

export async function apiFetch(path, init = {}) {
  // Demo mode has no identity and no backend — refuse before touching the
  // network. This is the single choke point for every authenticated call in
  // api.js, so it is what guarantees a demo bundle served from deskte.com
  // can never reach the real API.
  //
  // Rejecting (rather than returning an empty 200) is deliberate: it is
  // byte-for-byte the behaviour dev `?demo=1` has always had — getAccessToken()
  // throws because AuthGate never mounted to set _instance — and every caller
  // is already written to keep its seeded state when apiFetch rejects. An
  // empty-but-successful response would instead let callers overwrite the
  // SEED_* data with nothing, emptying the demo as the user clicks around.
  if (IS_DEMO) {
    // A few modules are server-owned (quotes, workflows, sequences, recurring
    // invoices, calendar) and would render empty with no backend at all.
    // demoApi.js answers just those endpoints from memory; everything else
    // still rejects.
    const canned = demoRespond(path, init);
    if (canned) return canned;
    throw new Error('Deskte demo mode: backend disabled');
  }

  const token = await getAccessToken();
  const headers = { ...(init.headers || {}), Authorization: `Bearer ${token}` };
  // Echo the single-session id when we have one. /auth/me uses presence /
  // absence of the header to decide: missing → fresh login (mint new id),
  // present and matching → same device (reuse), present and stale → reject
  // with SESSION_REPLACED so the older device is evicted.
  // Ver esperarSaludo: leer el id ANTES de que el saludo termine es lo que
  // hacía que la propia pestaña se expulsara al refrescar.
  await esperarSaludo(path);
  const sid = getSessionId();
  if (sid) headers['X-Session-Id'] = sid;
  // Ver getDeviceTag: el servidor lo guarda junto a la descripción del aparato
  // para que el aviso de expulsión pueda decir si fue ESTA misma computadora.
  const tag = getDeviceTag();
  if (tag) headers['X-Device-Tag'] = tag;
  // Browse-as-agency: only meaningful for platform owners; backend
  // ignores the header for everyone else.
  // View-as-user wins over browse-as-agency when both are somehow set.
  // X-Impersonate-Reason is paired with whichever id is sent — the
  // backend rejects (400 IMPERSONATION_REASON_REQUIRED) when the id is
  // valid but the reason is missing. Default 'session-resumed' covers
  // the rare case where localStorage was cleared between page loads but
  // the impersonate id survived.
  //
  // `sinSuplantar: true` = esta llamada va SIEMPRE como la persona real, sin
  // ninguna de estas cabeceras. Solo la usan los dos botones que EMPIEZAN un
  // "Entrar"/"Ver como" (api.js startBrowseAsAgency / startViewAsUser). Las
  // claves viven en localStorage, que comparten TODAS las pestañas: si en otra
  // pestaña se abrió un "Ver como", la pestaña de Platform Admin que ya estaba
  // abierta mandaba ese X-Impersonate-User, el servidor la trataba como esa
  // usuaria y requirePlatformOwner contestaba 403 — y no dejaba entrar a
  // ninguna agencia ni usuario (Lissette 2026-10-26). No amplía nada: sin
  // cabecera la petición va como quien es de verdad, y el servidor sigue
  // exigiendo PLATFORM_OWNER.
  const impersonateUserId = init.sinSuplantar === true ? null : getImpersonateUserId();
  const impersonateAgencyId = (init.sinSuplantar === true || impersonateUserId) ? null : getImpersonateAgencyId();
  if (impersonateUserId || impersonateAgencyId) {
    if (impersonateUserId) headers['X-Impersonate-User'] = impersonateUserId;
    else headers['X-Impersonate-Agency'] = impersonateAgencyId;
    // Sanitize on read too: a value stored by an older build (or hand-edited)
    // must not be able to make fetch() throw. If nothing survives, fall back
    // rather than send an empty header the backend would reject with 400.
    headers['X-Impersonate-Reason'] =
      toHeaderSafe(getImpersonateReason()) || 'session-resumed';
  }
  // Only a GET is replayed. Repeating a POST/PUT/DELETE that actually reached
  // the server — and whose ANSWER was what got lost — would create the ticket
  // twice, or send the client's email twice. A read has no such cost.
  const isRead = String(init.method || 'GET').toUpperCase() === 'GET';
  const msLimite = limiteDeTiempoPara(path, init);
  // Cuantas veces se ha reintentado POR TIEMPO AGOTADO, que no es lo mismo que
  // por un 503. Un 503 llega en un instante: repetirlo ocho veces cuesta nada
  // y es lo que salva un despliegue. Un intento que se paso de tiempo ya se
  // comio 45 segundos de la persona; repetirlo ocho veces son seis minutos
  // mirando una pantalla. Uno de esos, y basta.
  let reintentosPorTiempo = 0;
  const MAX_REINTENTOS_POR_TIEMPO = 1;
  let response;
  for (let attempt = 0; ; attempt++) {
    const reloj = relojDeIntento(init.signal, msLimite);
    try {
      response = await fetch(`${API_BASE}${path}`, { ...init, headers, signal: reloj.signal });
      // Contesto algo que no es "estoy reiniciando" -> el servidor esta vivo,
      // y el aviso se quita aunque el que lo puso fuera otra llamada.
      if (!isTransientStatus(response.status)) marcarServidorVivo();
      if (!isRead || !isTransientStatus(response.status) || attempt >= RETRY_BACKOFF_MS.length) {
        try { intentosDeLaPeticion.set(response, attempt + 1); } catch (_) { /* nunca puede tumbar la llamada */ }
        break;
      }
      marcarReintentando();
    } catch (err) {
      // El llamante se fue (componente desmontado, busqueda superada). No es un
      // fallo que contar, y desde luego no es uno que reintentar.
      if (init.signal?.aborted) throw err;
      // Se paso de tiempo. Llega como AbortError igual que el de arriba, y el
      // reloj es lo unico que los distingue.
      if (reloj.seAgotoElTiempo()) {
        const puedeReintentar = isRead
          && reintentosPorTiempo < MAX_REINTENTOS_POR_TIEMPO
          && attempt < RETRY_BACKOFF_MS.length;
        if (!puedeReintentar) {
          marcarServidorVivo();
          avisarQueTardoDemasiado();
          const seAcabo = new Error(`Deskte: la peticion a ${path} tardo mas de ${Math.round(msLimite / 1000)} s`);
          seAcabo.name = 'DeskteTimeoutError';
          seAcabo.code = 'TIMEOUT';
          throw seAcabo;
        }
        reintentosPorTiempo += 1;
        marcarReintentando();
      } else {
        if (err?.name === 'AbortError') throw err;
        if (!isRead || attempt >= RETRY_BACKOFF_MS.length) {
          // Se acabo la paciencia: se quita el aviso tranquilo y habla el rojo.
          // Si otra llamada sigue reintentando, lo volvera a poner en su
          // siguiente vuelta — el estado se recompone solo.
          marcarServidorVivo();
          if (isRead) noticeServerUnreachable();
          throw err;
        }
        marcarReintentando();
      }
    } finally {
      reloj.soltar();
    }
    await new Promise((r) => setTimeout(r, RETRY_BACKOFF_MS[attempt]));
  }
  // Out of attempts and still a bad gateway: say so out loud. Staying quiet
  // here is the actual bug being fixed — the caller will keep its empty list
  // and nothing on screen would ever explain why.
  if (isRead && isTransientStatus(response.status)) { marcarServidorVivo(); noticeServerUnreachable(); }

  // Modo solo lectura. Se mira el cuerpo en un CLON para no gastarlo: el que
  // llamo sigue leyendo su respuesta como siempre y su error se propaga tal
  // cual. Esto solo anade el aviso; no cambia lo que recibe nadie.
  if (response.status === 403) {
    try {
      const body = await response.clone().json();
      if (body && body.code === 'READ_ONLY') avisarSoloLectura(body.error);
      // AGENCIA EN PAUSA a mitad de sesión (Lissette la acaba de pausar): se
      // recarga UNA vez y /auth/me enseña la pantalla de "cuenta en pausa".
      // Nunca desde /auth/me mismo: esa respuesta ya pinta la pantalla, y
      // recargar desde ahí sería un bucle.
      if (body && body.code === 'AGENCY_PAUSED' && !String(path || '').startsWith('/auth/me') && !_recargandoPorPausa) {
        _recargandoPorPausa = true;
        window.location.reload();
      }
    } catch (_) {
      // 403 sin JSON (o de otra cosa). No es asunto de este bloque.
    }
  }

  // HIPAA idle timeout (SESSION_TIMEOUT) and single-active-session eviction
  // (SESSION_REPLACED) both force a re-login. Body inspection on a clone
  // keeps the original response usable by the caller.
  if (response.status === 401) {
    try {
      const body = await response.clone().json();
      const code = body && body.code;
      const evicted = code === 'SESSION_TIMEOUT' || code === 'SESSION_REPLACED';
      // Drop the dead id BEFORE the one-shot latch, and regardless of it. The
      // latch only exists to keep concurrent requests from each firing their
      // own redirect; when it was also gating this line, a second 401 (the
      // heartbeat and /auth/me racing) left the stale id in localStorage — so
      // "Reintentar" replayed the same rejected handshake and the user was
      // stuck on the error screen. Clearing it always means the retry and the
      // next sign-in start from a clean fresh-login handshake.
      // Dejar rastro de la expulsión. Hasta ahora el 401 se manejaba de
      // maravilla y NO llegaba a ningún sitio donde se pudiera contar, así
      // que a la pregunta "¿sigue pasando?" solo se podía contestar con
      // teorías (Lissette 2026-08-19). Va sin ningún dato personal: la ruta,
      // si se presentó cabecera y si la cuenta de MSAL se pudo resolver —
      // que es exactamente lo que distingue "otro dispositivo de verdad" de
      // "el navegador perdió el hilo".
      if (code === 'SESSION_REPLACED') {
        try {
          reportFrontendError(
            new Error(`Single-session eviction on ${path} (header=${sid ? 'sent' : 'absent'}, account=${activeAccountId() ? 'resolved' : 'unresolved'})`),
            'session-replaced',
          );
        } catch (_) { /* nunca puede tumbar el manejo del 401 */ }
      }
      if (code === 'MFA_REQUIRED' && _mfaRequiredHandler) {
        try { _mfaRequiredHandler(); } catch (err) { console.warn('[AuthGate] mfa handler failed:', err?.message); }
      }
      // La pantalla siempre manda X-Session-Id; si el servidor dice que faltó,
      // es un fallo nuestro y tiene que verse (auditoría 2026-09-27, punto 5).
      if (code === 'SESSION_REQUIRED') {
        try {
          reportFrontendError(new Error(`Request without X-Session-Id on ${path} (sid=${sid ? 'present' : 'absent'})`), 'session-required');
        } catch (_) { /* nunca puede tumbar el manejo del 401 */ }
      }
      if (code === 'MFA_ENROLL_REQUIRED' && _mfaEnrollHandler) {
        try { _mfaEnrollHandler(); } catch (err) { console.warn('[AuthGate] mfa enroll handler failed:', err?.message); }
      }
      if (evicted) { try { setSessionId(null); } catch (_) {} }
      if (evicted && !_sessionTimedOut && _instance) {
        _sessionTimedOut = true;
        if (code === 'SESSION_REPLACED') {
          _sessionReplacedBy = (body && body.replacedBy) || null;
        }
        if (code === 'SESSION_REPLACED' && _sessionReplacedHandler) {
          // Hand off to the React modal in AuthGate. The modal renders
          // a single "Entendido" button that triggers logoutRedirect on
          // click — that way the user is told WHY before being booted.
          _sessionReplacedHandler();
        } else {
          // Either SESSION_TIMEOUT (no message needed) or AuthGate hasn't
          // wired its handler yet → fall back to the original behavior.
          if (code === 'SESSION_REPLACED') {
            try {
              alert('Tu sesión se cerró porque iniciaste sesión en otro dispositivo.');
            } catch (_) {}
          }
          try { _instance.setActiveAccount(null); } catch (_) {}
          try { sessionStorage.clear(); } catch (_) {}
          _instance.logoutRedirect();
        }
      }
    } catch (_) {
      // Non-JSON 401 (e.g. plain "Invalid token"). No action — caller handles.
    }
  }

  return response;
}

// Map backend role → frontend role used by the existing app.
//
// PLATFORM_OWNER (Lissette as Deskte SaaS admin) is mapped to 'solo_agent'
// only so the existing UI shell renders without crashing. The actual
// behavior is gated by `isPlatformOwner` below: deskte-full.jsx hides the
// CRM nav for platform owners and forces the view to "platform_admin".
// This means Lissette sees ONLY the Platform Admin console with this
// account; her DalcerServices CRM lives under a separate AGENCY_OWNER
// account she registers through the normal sign-up flow.
const roleMap = {
  PLATFORM_OWNER: 'solo_agent', // Pure shell-rendering shim; real gating uses isPlatformOwner
  AGENCY_OWNER: 'agency_owner',
  AGENCY_ADMIN: 'agency_admin',
  AGENT: 'agent',
  SOLO_AGENT: 'solo_agent',
  // Backend enum was renamed ASSISTANT → SEAT (2026-05-06). Frontend keeps the
  // legacy "assistant" role string for backward compat with all components
  // that already check `role === "assistant"`.
  SEAT: 'assistant',
  ASSISTANT: 'assistant', // legacy fallback in case any DB row still has the old value
};

// Language for screens that render BEFORE /auth/me answered (so
// currentUser.languagePreference doesn't exist yet). Spanish is the default
// on anything unreadable — that's the userbase.
function browserLang() {
  try {
    return String(navigator.language || '').toLowerCase().startsWith('en') ? 'en' : 'es';
  } catch (_) { return 'es'; }
}

function initialsFrom(name = '', email = '') {
  const src = name.trim() || email.split('@')[0];
  const parts = src.split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || src.slice(0, 2).toUpperCase();
}

export function AuthGate({ children }) {
  const { instance, inProgress } = useMsal();
  const isAuthed = useIsAuthenticated();
  const [me, setMe] = useState(null);
  const [meErr, setMeErr] = useState(null);
  const [loading, setLoading] = useState(true);
  // Triggered by apiFetch when the backend answers SESSION_REPLACED — shows
  // the "Has iniciado sesión en otro dispositivo" modal in place of the app.
  const [sessionReplaced, setSessionReplacedState] = useState(false);
  // L-N3: documents whose current version this user has not yet accepted.
  // Populated by GET /auth/legal-status after /auth/me succeeds. When
  // non-empty, AuthGate shows a blocking modal in place of the app.
  const [legalStatus, setLegalStatus] = useState(null);
  // Autenticador: `mfaPasado` = esta pestaña ya dio el código; `mfaPedido` =
  // una llamada volvió con 401 MFA_REQUIRED aunque /auth/me dijera que no hacía
  // falta (p. ej. se encendió el interruptor de nuevo en el servidor).
  const [mfaPasado, setMfaPasado] = useState(false);
  const [mfaPedido, setMfaPedido] = useState(false);
  // Obligatorio y sin configurar: `mfaAltaHecha` = ya lo configuró en esta
  // pestaña; `mfaAltaPedida` = una llamada volvió con MFA_ENROLL_REQUIRED.
  const [mfaAltaHecha, setMfaAltaHecha] = useState(false);
  const [mfaAltaPedida, setMfaAltaPedida] = useState(false);
  const [joinToken, setJoinToken] = useState(() => getJoinTokenFromPath());
  const [quoteToken] = useState(() => getQuoteTokenFromPath());
  const [renewalReviewToken] = useState(() => getRenewalReviewTokenFromPath());
  const [encuestaToken] = useState(() => getEncuestaTokenFromPath());
  const [resourceLinkToken] = useState(() => getResourceLinkTokenFromPath());
  const [uploadToken] = useState(() => getUploadTokenFromPath());
  const [shareToken] = useState(() => getShareTokenFromPath());
  const [invoiceToken] = useState(() => getInvoiceTokenFromPath());
  const [signToken] = useState(() => getSignTokenFromPath());
  const [resourcePortalToken] = useState(() => getResourcePortalTokenFromPath());
  const [formSlug] = useState(() => getPublicFormSlugFromPath());
  const [portalToken] = useState(() => getPortalTokenFromPath());
  const [bookingSlug] = useState(() => getBookingSlugFromPath());
  const [cardSlug] = useState(() => getCardSlugFromPath());
  const [bookingManageToken] = useState(() => getBookingManageTokenFromPath());
  const [intakeToken] = useState(() => getIntakeTokenFromPath());
  const [applicationToken] = useState(() => getApplicationTokenFromPath());

  // Save instance for getAccessToken/apiFetch helpers
  useEffect(() => { _instance = instance; }, [instance]);

  // Register the SESSION_REPLACED handler so apiFetch can ask us to show
  // the modal instead of firing a native alert(). Unregistered on unmount
  // so the next AuthGate mount (post-logout flow) starts clean.
  useEffect(() => {
    setSessionReplacedHandler(() => setSessionReplacedState(true));
    return () => setSessionReplacedHandler(null);
  }, []);

  useEffect(() => {
    setMfaRequiredHandler(() => { setMfaPasado(false); setMfaPedido(true); });
    setMfaEnrollHandler(() => { setMfaAltaHecha(false); setMfaAltaPedida(true); });
    return () => { setMfaRequiredHandler(null); setMfaEnrollHandler(null); };
  }, []);

  // Background single-session heartbeat. Polls /auth/session-check every
  // 10 s while the user is logged in — the moment another device rotates
  // activeSessionId on the server, the next heartbeat comes back with
  // 401 SESSION_REPLACED and apiFetch's interceptor fires the modal.
  // Without this poll the eviction would only happen on the user's next
  // action, which could be 30+ minutes after the actual login elsewhere.
  useEffect(() => {
    if (!me || sessionReplaced) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      // apiFetch handles the SESSION_REPLACED response itself; we just
      // need to fire the request and swallow network errors so a flaky
      // connection doesn't take down the poller.
      try { await apiFetch('/auth/session-check'); } catch (_) {}
    };
    const id = setInterval(tick, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, [me, sessionReplaced]);

  // After login, set active account
  useEffect(() => {
    const accounts = instance.getAllAccounts();
    if (accounts.length > 0 && !instance.getActiveAccount()) {
      instance.setActiveAccount(accounts[0]);
    }
  }, [instance, isAuthed]);

  // Load /auth/me when authenticated
  useEffect(() => {
    let cancelled = false;
    if (!isAuthed || inProgress !== InteractionStatus.None) return;
    // UNA SESIÓN EXPULSADA NO SE ROBA LA SESIÓN DE VUELTA ELLA SOLA.
    //
    // Este efecto se repite cada vez que MSAL renueva el permiso en segundo
    // plano. Si la pestaña ya fue expulsada, su id quedó borrado, así que ese
    // /auth/me sale sin cabecera y el servidor EMITE una sesión nueva: la
    // pestaña perdedora se la quita a la ganadora sin que nadie toque el
    // teclado. La otra hace lo mismo al rato, y así sin parar — es el tira y
    // afloja cada pocos minutos que sufrió Lissette todo el día (2026-08-19,
    // resuelto cuando encontró su sesión de AYER abierta en otra PC).
    //
    // Quien pierde se queda fuera hasta que una PERSONA decida: el botón
    // "Entendido" cierra sesión y entra limpio. Recuperar el sitio no puede
    // ser algo que haga el navegador solo.
    if (sessionReplaced) return;

    (async () => {
      // Detect an account switch in THIS browser BEFORE the first call.
      // If the active MSAL account differs from the one we last
      // bootstrapped, wipe the prior user's single-session id +
      // impersonation so /auth/me goes in as a clean fresh login (no
      // X-Session-Id → backend mints a new id instead of evicting us).
      //
      // SOLO CON LA CUENTA DE VERDAD (Lissette 2026-10-26: "Entrar" / "Ver
      // como" volvían a la plataforma al recargar). Antes, sin cuenta activa
      // todavía, se tomaba la PRIMERA de las guardadas: con dos cuentas de
      // Microsoft en el navegador podía no ser la suya, esto lo leía como un
      // cambio de cuenta y borraba la entrada recién empezada. Ahora: la
      // cuenta activa, o la única que haya; si hay varias y ninguna activa, no
      // se decide nada. Y la primera vez (sin cuenta anotada) solo se anota:
      // no hay "cuenta anterior" de la que limpiar nada.
      try {
        const todas = instance.getAllAccounts();
        const acct = instance.getActiveAccount() || (todas.length === 1 ? todas[0] : null);
        const acctId = acct?.localAccountId || acct?.homeAccountId || null;
        let lastAcct = null;
        try { lastAcct = localStorage.getItem(LAST_ACCOUNT_KEY); } catch (_) {}
        if (acctId && lastAcct && acctId !== lastAcct) resetClientAuthStateForAccountSwitch();
        if (acctId && acctId !== lastAcct) {
          try { localStorage.setItem(LAST_ACCOUNT_KEY, acctId); } catch (_) {}
        }
      } catch (_) { /* non-fatal — fall through to normal load */ }

      // Una pestaña que arranca SIN poder resolver su cuenta manda /auth/me sin
      // cabecera, y eso hace que el servidor EMITA UNA SESIÓN NUEVA — con lo
      // que la pestaña que ya estaba abierta se lleva la expulsión. Lo peor:
      // el CRM se abre pestañas a sí mismo (desde Reportes y Comisiones, con
      // `window.open(...#client/<id>)`), así que la persona ve "iniciaste
      // sesión en otro dispositivo" sin haber abierto nada — solo hizo clic en
      // un nombre (Lissette 2026-08-19: "no estoy abriendo en otro lugar").
      //
      // Emitir una sesión nueva es correcto para una entrada de verdad, pero
      // aquí no hay ninguna: es la misma persona, el mismo navegador y la
      // sesión ya existe. Así que se ESPERA a que MSAL resuelva la cuenta —lo
      // normal es que tarde milisegundos— en vez de preguntar sin identificarse.
      //
      // Con tope: si a los ~3 s no resuelve, se sigue igual. Quedarse en la
      // pantalla de carga para siempre sería peor que una expulsión.
      for (let intento = 0; intento < 12 && !activeAccountId(); intento++) {
        await new Promise((r) => setTimeout(r, 250));
        if (cancelled) return;
      }

      setLoading(true);
      setMeErr(null);
      abrirSaludo();
      try {
        // Serialized across tabs: two simultaneous boots must not mint two
        // ids and leave the browser echoing the loser (see
        // withBootHandshakeLock).
        const r = await withBootHandshakeLock(() => apiFetch('/auth/me'));
        const json = await r.json();
        if (!r.ok) {
          if (cancelled) return;
          setMeErr({ status: r.status, ...json });
          setMe(null);
        } else if (!cancelled) {
          const u = json.data;
          limpiarEntradaQueNoEsDeEstaCuenta(u);
          // Persist the single-active-session id minted by the backend.
          // Any subsequent apiFetch echoes it via X-Session-Id; an older
          // device that logged in earlier will be evicted on its next call.
          if (u.sessionId) setSessionId(u.sessionId);
          // Ya hay id bueno: se abre la puerta a las demás llamadas.
          cerrarSaludo();
          const mapped = {
            id: u.id,
            name: u.displayName || u.email,
            email: u.email,
            role: roleMap[u.role] || 'solo_agent',
            initials: initialsFrom(u.displayName, u.email),
            color: '#00d4ff',
            agencyId: u.agency?.id || null,
            // Lissette 2026-05-27: PDF/print templates read
            // currentUser.agency.logoUrl. Carry the whole agency object
            // forward so the print/quote/invoice flows can brand on the
            // fly without an extra fetch.
            agency: u.agency || null,
            languagePreference: u.languagePreference || 'en',
            // UI appearance prefs (persisted server-side so they follow the
            // user across devices). Null until the user has saved a choice.
            uiTheme: u.uiTheme || null,
            uiPalette: u.uiPalette || null,
            // Misc per-user UI prefs (favorites, collapsed nav, announce seen)
            // — synced across devices via /auth/me.
            uiPrefs: (u.uiPrefs && typeof u.uiPrefs === 'object') ? u.uiPrefs : {},
            // Internal flag — true only for Lissette as Deskte founder. Unlocks
            // the "Platform Admin" sidebar item that renders PlatformAdminView.
            isPlatformOwner: u.role === 'PLATFORM_OWNER',
            // True only while a platform owner is viewing-as this user. The
            // rendered role is the target's (so seat gating applies), so
            // this is the only signal the SPA has to show the Exit banner.
            impersonatedByPlatform: u.impersonatedByPlatform === true,
            // Seat → supervising agent link. The legacy mailbox toggles
            // (view inbox / send on behalf) were removed 2026-05-17.
            supervisorId: u.supervisorId || null,
            supervisor: u.supervisor || null, // { id, displayName, email } if supervisor exists
            // Seat profile — sidebar gating + per-module access (HIPAA Min Necessary).
            seatLabel: u.seatLabel || null,
            seatPermissions: u.seatPermissions && typeof u.seatPermissions === 'object' ? u.seatPermissions : null,
            // El ALCANCE del asiento, no solo sus permisos. Hace falta en
            // pantalla para explicar una lista vacía: un asiento de "solo sus
            // prospectos" empieza sin ninguna ficha y un vacío sin explicación
            // se lee como avería. Va AQUÍ, en esta lista explícita, por lo que
            // avisa el comentario de tres líneas más abajo.
            seatScope: u.seatScope || null,
            // Agent profile — server-backed so it syncs across devices and
            // feeds defaults (e.g. license NPN). `title` maps to jobTitle.
            npn: u.npn || '',
            // Correo de CONTACTO (firma y materiales). Distinto de `email`, que es
            // la identidad de acceso y no se edita desde el perfil.
            contactEmail: u.contactEmail || '',
            // OJO: esta lista es EXPLÍCITA. Un campo nuevo del perfil que no se
            // nombre aquí se guarda bien en el servidor y desaparece al
            // recargar — el formulario vuelve vacío y la vista previa de la
            // firma no tiene qué pintar. Pasó con estos dos el 2026-08-19:
            // estaban en el formulario, en el envío y en el servidor, y
            // faltaban justo en esta línea. Son CUATRO capas: formulario →
            // envío → servidor → esta vuelta.
            contactEmail2: u.contactEmail2 || '',
            officePhone: u.officePhone || '',
            // WhatsApp y fax (Lissette 2026-08-29). Nacen aquí nombrados a
            // propósito: es justo la línea que se olvidó en 2026-08-19.
            whatsappNumber: u.whatsappNumber || '',
            fax: u.fax || '',
            title: u.jobTitle || '',
            companyName: u.companyName || '',
            // Per-agent override of the shared agency name on this agent's own
            // client-facing materials. Empty → uses the real agency name.
            agencyDisplayName: u.agencyDisplayName || '',
            // El nombre con que el cliente ve al agente en su portal (2026-10-20).
            portalDisplayName: u.portalDisplayName || '',
            companyLogo: u.companyLogo || '',
            website: u.website || '',
            bio: u.bio || '',
            licenseStates: u.licenseStates || '',
            // Social profiles — stored already normalized to an absolute URL,
            // so the card/signature use them verbatim as the icon href.
            instagramUrl: u.instagramUrl || '',
            facebookUrl: u.facebookUrl || '',
            linkedinUrl: u.linkedinUrl || '',
            tiktokUrl: u.tiktokUrl || '',
            profilePhoto: u.profilePhoto || '',
            mailingStreet: u.mailingStreet || '',
            mailingCity: u.mailingCity || '',
            mailingState: u.mailingState || '',
            mailingZip: u.mailingZip || '',
            mailingAddress: u.mailingAddress || '',
            phone: u.phone || '',
            _backend: u,
          };
          setMe(mapped);
        }
      } catch (e) {
        if (!cancelled) setMeErr({ message: e.message });
      } finally {
        // SIEMPRE. Si el saludo falla, las demás llamadas no pueden quedarse
        // esperando: pasarían los 8 s de tope y saldrían igual, pero tarde y
        // sin motivo. Abrir la puerta al terminar —bien o mal— es lo correcto.
        cerrarSaludo();
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isAuthed, inProgress, sessionReplaced]);

  // L-N3: once /auth/me succeeds, ask the backend which legal documents
  // the current user still owes a fresh acceptance for. Platform owner
  // returns an empty list so the modal never fires for Lissette. A
  // network failure here keeps `legalStatus` null and the app proceeds —
  // we treat consent enforcement as best-effort to avoid bricking the
  // CRM when a transient error hits.
  useEffect(() => {
    let cancelled = false;
    if (!me) { setLegalStatus(null); return; }
    if (me.impersonatedByPlatform) {
      // Lissette viewing-as a target user must not be forced to
      // re-accept on their behalf.
      setLegalStatus(null);
      return;
    }
    (async () => {
      try {
        const status = await getLegalStatus();
        if (!cancelled) setLegalStatus(status || null);
      } catch (e) {
        console.warn('[AuthGate] legal-status fetch failed:', e.message);
        if (!cancelled) setLegalStatus(null);
      }
    })();
    return () => { cancelled = true; };
  }, [me]);

  const handleLogin = useCallback(() => {
    // `prompt: 'select_account'` forces Microsoft to show the account picker
    // even if a user is already signed-in to a different account in the
    // browser. Without this, MSAL silently re-auths with the last account —
    // which prevents switching from agent → seat (or vice versa).
    instance.loginRedirect({ ...loginRequest, prompt: 'select_account' }).catch((e) => {
      console.error('[AuthGate] loginRedirect failed:', e);
    });
  }, [instance]);

  const handleLogout = useCallback(() => {
    // Clear MSAL caches so the next sign-in starts from a clean slate.
    // Without clearing the active account, MSAL can auto-resume the previous
    // session even after `logoutRedirect`, which surprised the user.
    try { setSessionId(null); } catch (_) {}
    try { localStorage.removeItem(LAST_ACCOUNT_KEY); } catch (_) {}
    borrarEntrada(IMPERSONATE_KEY);
    borrarEntrada(IMPERSONATE_USER_KEY);
    borrarEntrada(IMPERSONATE_REASON_KEY);
    try { instance.setActiveAccount(null); } catch (_) {}
    try { sessionStorage.clear(); } catch (_) {}
    instance.logoutRedirect();
  }, [instance]);

  // OAuth callback popup — closes itself + posts result back to opener.
  // Must short-circuit before any auth logic since this URL has no token.
  if (isOAuthCallbackPath()) {
    return <OAuthCallbackPage />;
  }
  // Public lead-capture form landing — no auth, slug-protected on the backend.
  if (formSlug) {
    return <PublicFormPage slug={formSlug} />;
  }
  // Public booking management (reschedule/cancel) — no auth, token in email.
  if (bookingManageToken) {
    return <BookingManagePage token={bookingManageToken} />;
  }
  // Public booking landing — no auth, slug-protected on the backend.
  if (bookingSlug) {
    return <BookingPublicPage slug={bookingSlug} />;
  }
  // Tarjeta digital publica — sin auth, el slug es la llave.
  if (cardSlug) {
    return <AgentCardPage slug={cardSlug} />;
  }
  // Client portal landing — no auth, token-protected.
  if (portalToken) {
    return <ClientPortalPage token={portalToken} />;
  }
  // Renewal-review landing — no auth, token-protected.
  if (renewalReviewToken) {
    return <RenewalReviewPage token={renewalReviewToken} />;
  }
  // Encuesta al cliente — sin sesión, el token es la llave.
  if (encuestaToken) {
    return <EncuestaPublicPage token={encuestaToken} />;
  }
  // Public single-resource link — bounce straight to the API, which validates
  // the signature and redirects to the file. Rendered as a bare "opening…" so
  // a slow network doesn't look like a broken link.
  if (resourceLinkToken) {
    const apiBase = import.meta.env.VITE_API_URL || '';
    window.location.replace(`${apiBase}/r/${resourceLinkToken}`);
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#070d14', color: '#7aa3c0', fontFamily: 'system-ui', fontSize: '.9rem' }}>
        Abriendo el documento…
      </div>
    );
  }
  // Public quote landing page — no auth needed, takes precedence over everything.
  if (quoteToken) {
    return <QuoteLandingPage token={quoteToken} />;
  }
  // Public enrollment form (after accepting a plan) — no auth, token-protected.
  if (intakeToken) {
    return <IntakePublicPage token={intakeToken} />;
  }
  // Public application form — no auth, token-protected.
  if (applicationToken) {
    return <ApplicationPublicPage token={applicationToken} />;
  }
  // Public document upload portal — no auth required.
  if (uploadToken) {
    return <DocPortalLandingPage token={uploadToken} />;
  }
  // Public OUTBOUND share — a carrier opening the link with a client's
  // documents. No auth required; the token is the credential.
  if (shareToken) {
    return <DocSharePage token={shareToken} />;
  }
  // Public invoice landing — recipient sees details + pays. No auth required.
  if (invoiceToken) {
    return <InvoicePortalLandingPage token={invoiceToken} />;
  }
  // Public signature portal — no auth required.
  if (signToken) {
    return <SignaturePortalPage token={signToken} />;
  }
  // Public client resource portal — no auth required (token-protected).
  if (resourcePortalToken) {
    return <PublicResourcePortal token={resourcePortalToken} />;
  }

  // While MSAL is processing the redirect callback
  if (inProgress !== InteractionStatus.None) {
    return <Splash msg="Procesando login…" />;
  }

  // Invitation flow takes precedence over normal sign-in.
  if (joinToken) {
    return (
      <JoinScreen
        token={joinToken}
        isAuthed={isAuthed}
        onLogin={handleLogin}
        onLogout={handleLogout}
        accountEmail={instance.getActiveAccount()?.username}
        onAccepted={() => {
          sessionStorage.removeItem('deskte:pendingInviteToken');
          // Force /auth/me reload so AuthGate picks up the new agencyId/role
          setJoinToken(null);
          window.history.replaceState({}, '', '/');
          window.location.reload();
        }}
        onCancel={() => {
          sessionStorage.removeItem('deskte:pendingInviteToken');
          setJoinToken(null);
          window.history.replaceState({}, '', '/');
        }}
      />
    );
  }

  if (!isAuthed) {
    return <SignInScreen onLogin={handleLogin} />;
  }

  if (loading) {
    return <Splash msg="Cargando perfil…" />;
  }

  // Single-active-session eviction. It reaches us two ways:
  //   - a LATER call was evicted → apiFetch's interceptor set `sessionReplaced`
  //   - /auth/me ITSELF was evicted at boot → `me` is null and the code only
  //     lives on `meErr`. That path used to fall through to the generic
  //     ErrorScreen, so instead of the message the user got a raw JSON 401 dump
  //     under "No pudimos cargar tu perfil" (Lissette 2026-08-17, con captura).
  //     The friendly screen was effectively unreachable at login.
  // Rendered before `window.__deskteAuth` is injected, so the app never gets a
  // render with a known-evicted session.
  if (sessionReplaced || meErr?.code === 'SESSION_REPLACED') {
    return (
      <SessionReplacedScreen
        lang={me?.languagePreference || browserLang()}
        replacedBy={getSessionReplacedBy() || meErr?.replacedBy || null}
        deviceTag={getDeviceTag()}
        onConfirm={() => {
          setSessionReplacedState(false);
          handleLogout();
        }}
      />
    );
  }

  // AGENCIA EN PAUSA (Lissette 2026-10-22): la agencia pidió congelar su
  // cuenta. No es un error y no se enseña como tal.
  if (meErr?.code === 'AGENCY_PAUSED') {
    return (
      <AgencyPausedScreen
        lang={browserLang()}
        pausedUntil={meErr.pausedUntil || null}
        onLogout={handleLogout}
      />
    );
  }

  if (meErr?.needsRegistration) {
    return (
      <NeedsRegistrationScreen
        onLogout={handleLogout}
        email={instance.getActiveAccount()?.username}
        displayName={instance.getActiveAccount()?.name}
        onRegistered={() => {
          // /auth/register succeeded → reload so AuthGate re-fetches /auth/me
          // with the freshly-created user + agency.
          window.location.reload();
        }}
      />
    );
  }

  if (meErr) {
    return <ErrorScreen err={meErr} onLogout={handleLogout} onRetry={() => window.location.reload()} />;
  }

  if (!me) return <Splash msg="Cargando…" />;

  // Autenticador: quien lo tiene activo da su código una vez por sesión, ANTES
  // de ver nada del CRM (y antes de los términos: primero se sabe quién es).
  // La demo no tiene servidor ni cuentas, así que nunca llega aquí.
  const mfaSesion = me._backend?.mfa;
  // Obligatorio para todos y aún no lo tiene: primero el QR. Quien "ve como"
  // otro usuario no llega aquí por el de ese usuario: `mfa` es de la persona real.
  const faltaAlta = !IS_DEMO && !mfaAltaHecha
    && (mfaAltaPedida || (mfaSesion?.required === true && mfaSesion?.enabled !== true));
  if (faltaAlta) {
    return (
      <AutenticadorAltaScreen
        lang={me.languagePreference}
        email={me.email}
        onDone={() => { setMfaAltaPedida(false); setMfaAltaHecha(true); setMfaPedido(false); setMfaPasado(true); }}
        onLogout={handleLogout}
      />
    );
  }
  const faltaCodigo = !IS_DEMO && !mfaPasado
    && (mfaPedido || (mfaSesion?.enabled === true && mfaSesion?.verified !== true));
  if (faltaCodigo) {
    return (
      <AutenticadorCodigoScreen
        lang={me.languagePreference}
        email={me.email}
        onVerified={() => { setMfaPedido(false); setMfaPasado(true); }}
        onLogout={handleLogout}
      />
    );
  }

  // L-N3: blocking re-acceptance modal. Fires when the documents the
  // user previously accepted are stale (or, for users created during the
  // pre-launch placeholder window, never persisted at all). Sign-out
  // is the only escape; accepting writes a fresh row in legal_acceptances.
  const pendingLegal = legalStatus && Array.isArray(legalStatus.documents)
    ? legalStatus.documents.filter((d) => d.requiresAcceptance)
    : [];
  if (pendingLegal.length > 0) {
    return (
      <LegalReacceptanceScreen
        lang={me.languagePreference}
        documents={pendingLegal}
        onAccepted={async () => {
          try {
            const status = await getLegalStatus();
            setLegalStatus(status || null);
          } catch (e) {
            // Don't strand the user if the re-fetch fails — they just
            // accepted, so clear locally and trust the backend write.
            setLegalStatus({ documents: [], requiresAcceptance: [] });
          }
        }}
        onLogout={handleLogout}
      />
    );
  }

  // Inject the real user + helpers into the existing App via window globals.
  // The App reads currentUser from its own state, so we expose hooks the App can opt into.
  //
  // Audit H3: getAccessToken USED to live on this object. It was never read
  // by any component (every consumer goes through apiFetch, which calls
  // getAccessToken internally inside the closure), and exposing it on
  // window made a stored-XSS or rogue print-window able to mint a bearer
  // token against the API. Removed. apiFetch + currentUser + logout are
  // safe to expose — apiFetch doesn't return the token to its caller,
  // and currentUser carries no secrets.
  if (typeof window !== 'undefined') {
    window.__deskteAuth = {
      currentUser: me,
      logout: handleLogout,
      apiFetch,
    };
  }

  return children;
}

function SessionReplacedScreen({ lang, onConfirm, replacedBy, deviceTag }) {
  const es = lang === 'es';
  // "Se inició sesión en otro dispositivo" acusa de algo que la persona no
  // recuerda haber hecho, y sin más datos no hay forma de saber si fue su
  // propio teléfono o alguien más con su usuario. Cuando el servidor sabe
  // decirlo, se dice (Lissette 2026-08-19). Si no lo sabe —una sesión abierta
  // antes de que existieran estas columnas— esta línea simplemente no aparece:
  // mejor callarse que inventar.
  const detalle = (() => {
    if (!replacedBy) return null;
    const partes = [];
    if (replacedBy.device) partes.push(replacedBy.device);
    if (replacedBy.at) {
      const d = new Date(replacedBy.at);
      if (!Number.isNaN(d.getTime())) {
        // en-US siempre, como el resto del CRM, y en hora LOCAL de quien mira.
        partes.push(d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }));
      }
    }
    return partes.length ? partes.join(' · ') : null;
  })();
  return (
    <div style={baseStyle}>
      <div style={{
        maxWidth: 440, width: '100%',
        background: '#112538',
        border: '1px solid rgba(255, 89, 89, 0.35)',
        borderRadius: 14,
        padding: '2rem 1.75rem',
        textAlign: 'center',
        boxShadow: '0 24px 60px rgba(0, 0, 0, 0.4)',
      }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🔒</div>
        <h2 style={{
          color: '#eef6ff',
          fontFamily: 'Sora, sans-serif',
          fontWeight: 700,
          fontSize: '1.15rem',
          margin: '0 0 0.75rem',
        }}>
          {es ? 'Sesión cerrada' : 'Session closed'}
        </h2>
        <p style={{
          color: '#a5b4fc',
          fontSize: '0.88rem',
          lineHeight: 1.55,
          margin: '0 0 1.5rem',
        }}>
          {es
            ? 'Se inició sesión con tu cuenta en otro dispositivo. Por seguridad, esta sesión fue cerrada.'
            : 'Your account was used to sign in on another device. For security, this session has been closed.'}
        </p>
        {detalle && (
          <div style={{
            margin: '-0.75rem 0 1.25rem',
            padding: '0.6rem 0.75rem',
            borderRadius: 10,
            background: 'rgba(0, 212, 255, 0.08)',
            border: '1px solid rgba(0, 212, 255, 0.25)',
            color: '#c8dff0',
            fontSize: '0.8rem',
            lineHeight: 1.5,
          }}>
            <div style={{ fontWeight: 700, marginBottom: 2 }}>{es ? 'Se abrió en:' : 'Opened on:'}</div>
            <div>{detalle}</div>
            {/* La pregunta que de verdad importa: ¿fue este mismo equipo? El
                apodo del navegador la contesta sin lugar a dudas. */}
            {deviceTag && replacedBy?.device && (
              <div style={{ marginTop: 6, fontWeight: 700, color: replacedBy.device.includes(deviceTag) ? '#ffb700' : '#ff8a8a' }}>
                {replacedBy.device.includes(deviceTag)
                  ? (es ? '➜ Fue ESTA misma computadora (otra pestaña o ventana).'
                        : '➜ It was THIS same computer (another tab or window).')
                  : (es ? '➜ Fue OTRO equipo, no este.' : '➜ It was ANOTHER device, not this one.')}
              </div>
            )}
            <div style={{ marginTop: 6, color: '#7da0c4', fontSize: '0.72rem' }}>
              {es
                ? '¿No lo reconoces? Cambia tu contraseña y avisa a tu agencia.'
                : "Don't recognize it? Change your password and tell your agency."}
            </div>
          </div>
        )}
        <button
          onClick={onConfirm}
          style={{
            width: '100%',
            padding: '0.75rem',
            borderRadius: 10,
            background: 'linear-gradient(135deg, #0099cc, #0055aa)',
            border: 'none',
            color: '#fff',
            fontSize: '0.9rem',
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(0, 153, 204, 0.3)',
          }}
        >
          {es ? 'Entendido' : 'Got it'}
        </button>
        <div style={{
          fontSize: '0.65rem',
          color: '#45688a',
          marginTop: '0.95rem',
          letterSpacing: '0.05em',
        }}>
          {es ? 'Solo una sesión activa por usuario' : 'One active session per user'}
        </div>
      </div>
    </div>
  );
}

// Lo que ve quien intenta entrar a una agencia en pausa. Dice cuándo vuelve, si
// hay fecha, y a quién escribir para volver antes. Solo un botón: salir.
function AgencyPausedScreen({ lang, pausedUntil, onLogout }) {
  const es = lang === 'es';
  const fecha = (() => {
    if (!pausedUntil) return null;
    const d = new Date(pausedUntil);
    return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('en-US', { dateStyle: 'long' });
  })();
  return (
    <div style={baseStyle}>
      <div style={{
        maxWidth: 440, width: '100%',
        background: '#112538',
        border: '1px solid rgba(255, 183, 0, 0.35)',
        borderRadius: 14,
        padding: '2rem 1.75rem',
        textAlign: 'center',
        boxShadow: '0 24px 60px rgba(0, 0, 0, 0.4)',
      }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>⏸️</div>
        <h2 style={{ color: '#eef6ff', fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: '1.15rem', margin: '0 0 0.75rem' }}>
          {es ? 'Tu cuenta está en pausa' : 'Your account is paused'}
        </h2>
        <p style={{ color: '#a5b4fc', fontSize: '0.88rem', lineHeight: 1.55, margin: '0 0 1rem' }}>
          {es
            ? 'Tu cuenta de Deskte está en pausa. Tus clientes, pólizas y documentos están guardados y seguros; no se ha borrado nada.'
            : 'Your Deskte account is paused. Your clients, policies and documents are saved and safe; nothing has been deleted.'}
        </p>
        <p style={{ color: '#c8dff0', fontSize: '0.85rem', lineHeight: 1.55, margin: '0 0 1.5rem' }}>
          {fecha
            ? (es ? <>Vuelve a estar disponible el <b>{fecha}</b>. ¿La necesitas antes? Escríbenos a <b>hello@deskte.com</b>.</>
                  : <>It will be available again on <b>{fecha}</b>. Need it sooner? Write to <b>hello@deskte.com</b>.</>)
            : (es ? <>Para reactivarla, escríbenos a <b>hello@deskte.com</b>.</>
                  : <>To reactivate it, write to <b>hello@deskte.com</b>.</>)}
        </p>
        <button
          onClick={onLogout}
          style={{
            width: '100%', padding: '0.75rem', borderRadius: 10,
            background: 'linear-gradient(135deg, #0099cc, #0055aa)', border: 'none',
            color: '#fff', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(0, 153, 204, 0.3)',
          }}
        >
          {es ? 'Cerrar sesión' : 'Sign out'}
        </button>
      </div>
    </div>
  );
}

// L-N3 (audit 2026-05-20 v2): blocking re-acceptance modal. Renders the
// list of legal documents whose current version the user has not yet
// accepted, with a link to each and a single checkbox + Accept button.
// Sign-out is the only escape — the CRM stays hidden until the user
// either accepts or logs out. Backwards-compat for users created during
// the pre-launch placeholder window where signup persisted nothing.
function LegalReacceptanceScreen({ lang, documents, onAccepted, onLogout }) {
  // The modal blocks the CRM, so the user can't reach the in-app
  // language toggle from here. Render our own EN/ES switch and persist
  // the choice to the same `deskte-lang` localStorage key the rest of
  // the SPA reads, so flipping here also flips the CRM after they get
  // through this screen. Initial value: previously-saved toggle, or the
  // user's stored account preference, or English as the safe default.
  const [modalLang, setModalLang] = useState(() => {
    try {
      const saved = localStorage.getItem('deskte-lang');
      if (saved === 'es' || saved === 'en') return saved;
    } catch (_) { /* ignore */ }
    return lang === 'es' ? 'es' : 'en';
  });
  const setModalLangPersist = (next) => {
    setModalLang(next);
    try { localStorage.setItem('deskte-lang', next); } catch (_) { /* ignore */ }
  };
  const es = modalLang === 'es';

  // The BAA is pulled out of the generic list and given its own heading,
  // its own explanation and its own checkbox.
  //
  // It used to sit as one more <li> under a heading that reads "We updated our
  // terms", covered by a single "I have reviewed the documents above" tick.
  // Consent was genuine — the row was there with a Read link — but Lissette
  // accepted that screen and came away certain she had only agreed to the
  // Terms (2026-08-10). If the person who built the product misses it, every
  // agency owner will. A BAA is a HIPAA contract between two companies; "it
  // was in the list" is a far weaker answer to an auditor, or to a customer
  // who later disputes it, than a section they had to tick on its own.
  const baaDoc = documents.find((d) => d.documentType === 'baa') || null;
  const otherDocs = documents.filter((d) => d.documentType !== 'baa');

  const [agree, setAgree] = useState(false);
  const [agreeBaa, setAgreeBaa] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Each checkbox only gates the block it belongs to, so a screen showing just
  // one of the two never waits on a tick the user was never offered.
  const ready = (otherDocs.length === 0 || agree) && (!baaDoc || agreeBaa);

  const submit = useCallback(async () => {
    if (!ready || busy) return;
    setBusy(true);
    setErr(null);
    try {
      await acceptLegal(documents.map((d) => ({
        documentType: d.documentType,
        version: d.currentVersion,
      })));
      await onAccepted();
    } catch (e) {
      setErr(e.message || (es ? 'No se pudo registrar la aceptación' : 'Could not record acceptance'));
      setBusy(false);
    }
  }, [ready, busy, documents, es, onAccepted]);
  return (
    <div style={baseStyle}>
      <div style={{
        maxWidth: 520, width: '100%',
        background: '#112538',
        border: '1px solid rgba(0, 212, 255, 0.25)',
        borderRadius: 14,
        padding: '2rem 1.75rem',
        boxShadow: '0 24px 60px rgba(0, 0, 0, 0.4)',
        position: 'relative',
      }}>
        {/* EN / ES toggle — the modal blocks the CRM's own language
            switch, so we expose one here. Persists to the same
            `deskte-lang` key the rest of the SPA reads. */}
        <div style={{
          position: 'absolute',
          top: 12,
          right: 12,
          display: 'inline-flex',
          gap: 0,
          background: '#0d1c2e',
          border: '1px solid #1a3550',
          borderRadius: 8,
          overflow: 'hidden',
          fontSize: '0.7rem',
          fontWeight: 700,
          fontFamily: 'JetBrains Mono, monospace',
        }}>
          {['en', 'es'].map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => setModalLangPersist(code)}
              disabled={busy}
              style={{
                padding: '0.3rem 0.65rem',
                border: 'none',
                cursor: busy ? 'not-allowed' : 'pointer',
                background: modalLang === code ? '#00d4ff' : 'transparent',
                color: modalLang === code ? '#0a1628' : '#a5b4fc',
              }}
            >
              {code.toUpperCase()}
            </button>
          ))}
        </div>
        <div style={{ fontSize: '2rem', marginBottom: '0.4rem', textAlign: 'center' }}>📄</div>
        <h2 style={{
          color: '#eef6ff',
          fontFamily: 'Sora, sans-serif',
          fontWeight: 700,
          fontSize: '1.2rem',
          margin: '0 0 0.6rem',
          textAlign: 'center',
        }}>
          {otherDocs.length === 0
            ? (es ? 'Firma tu Acuerdo HIPAA' : 'Sign your HIPAA agreement')
            : baaDoc
              ? (es ? 'Dos documentos por firmar' : 'Two documents to sign')
              : (es ? 'Actualizamos nuestros términos' : 'We updated our terms')}
        </h2>
        <p style={{
          color: '#a5b4fc',
          fontSize: '0.85rem',
          lineHeight: 1.55,
          margin: '0 0 1.25rem',
          textAlign: 'center',
        }}>
          {es
            ? 'Necesitamos tu aceptación de la versión vigente antes de continuar. Esto se registra para cumplir con HIPAA y ESIGN Act.'
            : 'We need your acceptance of the current version before continuing. This is recorded for HIPAA and ESIGN Act compliance.'}
        </p>
        {otherDocs.length > 0 && (
        <>
        <ul style={{
          listStyle: 'none',
          padding: 0,
          margin: '0 0 1rem',
          background: '#0d1c2e',
          border: '1px solid #1a3550',
          borderRadius: 10,
          fontSize: '0.85rem',
        }}>
          {otherDocs.map((d, idx) => (
            <li key={d.documentType} style={{
              padding: '0.7rem 0.95rem',
              borderTop: idx === 0 ? 'none' : '1px solid #1a3550',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '0.5rem',
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: '#eef6ff', fontWeight: 600 }}>{es ? d.labelEs : d.labelEn}</div>
                <div style={{ fontSize: '0.7rem', color: '#45688a', fontFamily: 'JetBrains Mono, monospace' }}>
                  v{d.currentVersion}{d.acceptedVersion ? ` · ${es ? 'aceptó' : 'accepted'} v${d.acceptedVersion}` : ` · ${es ? 'no aceptada' : 'not accepted'}`}
                </div>
              </div>
              <a href={d.url} target="_blank" rel="noopener noreferrer" style={{
                color: '#00d4ff',
                fontSize: '0.75rem',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}>
                {es ? 'Leer' : 'Read'} →
              </a>
            </li>
          ))}
        </ul>
        <label style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.55rem',
          margin: '0 0 1rem',
          fontSize: '0.82rem',
          color: '#a5b4fc',
          lineHeight: 1.45,
          cursor: 'pointer',
        }}>
          <input
            type="checkbox"
            checked={agree}
            onChange={(e) => setAgree(e.target.checked)}
            style={{ marginTop: 3 }}
            disabled={busy}
          />
          <span>
            {es
              ? 'He revisado los documentos arriba y acepto la versión vigente.'
              : 'I have reviewed the documents above and accept the current version.'}
          </span>
        </label>
        </>
        )}

        {baaDoc && (
          <div style={{
            background: 'rgba(0, 212, 255, 0.05)',
            border: '1px solid rgba(0, 212, 255, 0.35)',
            borderRadius: 10,
            padding: '0.9rem 1rem',
            margin: '0 0 1rem',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '1.05rem' }}>🛡️</span>
              <span style={{ color: '#eef6ff', fontWeight: 700, fontSize: '0.92rem' }}>
                {es ? 'Acuerdo de Asociado de Negocios (BAA)' : 'Business Associate Agreement (BAA)'}
              </span>
            </div>
            <p style={{ color: '#a5b4fc', fontSize: '0.78rem', lineHeight: 1.55, margin: '0 0 0.7rem' }}>
              {es
                ? 'Este es un contrato HIPAA entre tu agencia y DALCERSERVICES LLC. Establece cómo protegemos la información médica de tus clientes y qué obligaciones tiene cada parte. Es obligatorio antes de manejar datos de salud en Deskte.'
                : 'This is a HIPAA contract between your agency and DALCERSERVICES LLC. It sets out how we protect your clients’ health information and what each party is responsible for. It is required before handling health data in Deskte.'}
            </p>
            <a
              href={baaDoc.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#00d4ff', fontSize: '0.78rem', fontWeight: 700 }}
            >
              {es ? 'Leer el acuerdo completo' : 'Read the full agreement'} →
            </a>
            <div style={{ fontSize: '0.68rem', color: '#45688a', fontFamily: 'JetBrains Mono, monospace', margin: '0.45rem 0 0.75rem' }}>
              v{baaDoc.currentVersion}
            </div>
            <label style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.55rem',
              fontSize: '0.82rem',
              color: '#eef6ff',
              lineHeight: 1.45,
              cursor: 'pointer',
              fontWeight: 600,
            }}>
              <input
                type="checkbox"
                checked={agreeBaa}
                onChange={(e) => setAgreeBaa(e.target.checked)}
                style={{ marginTop: 3 }}
                disabled={busy}
              />
              <span>
                {es
                  ? 'He leído el BAA y lo firmo en nombre de mi agencia.'
                  : 'I have read the BAA and sign it on behalf of my agency.'}
              </span>
            </label>
          </div>
        )}
        {err && (
          <div style={{
            background: 'rgba(255, 89, 89, 0.12)',
            border: '1px solid rgba(255, 89, 89, 0.3)',
            color: '#ff7a7a',
            padding: '0.55rem 0.8rem',
            borderRadius: 8,
            fontSize: '0.78rem',
            marginBottom: '0.85rem',
          }}>{err}</div>
        )}
        <div style={{ display: 'flex', gap: '0.6rem' }}>
          <button
            type="button"
            onClick={onLogout}
            disabled={busy}
            style={{
              flex: '0 0 auto',
              padding: '0.7rem 1rem',
              borderRadius: 10,
              background: 'transparent',
              border: '1px solid #1a3550',
              color: '#a5b4fc',
              fontSize: '0.82rem',
              fontWeight: 600,
              cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >
            {es ? 'Cerrar sesión' : 'Sign out'}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!ready || busy}
            style={{
              flex: 1,
              padding: '0.7rem 1rem',
              borderRadius: 10,
              background: ready && !busy ? 'linear-gradient(135deg, #0099cc, #0055aa)' : '#1a3550',
              border: 'none',
              color: '#fff',
              fontSize: '0.9rem',
              fontWeight: 700,
              cursor: ready && !busy ? 'pointer' : 'not-allowed',
              boxShadow: ready && !busy ? '0 4px 14px rgba(0, 153, 204, 0.3)' : 'none',
            }}
          >
            {busy
              ? (es ? 'Registrando…' : 'Recording…')
              : baaDoc
                ? (es ? 'Firmar y continuar' : 'Sign and continue')
                : (es ? 'Aceptar y continuar' : 'Accept and continue')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── UI bits ──────────────────────────────────────────────────────────────────

const baseStyle = {
  minHeight: '100vh',
  background: '#070d14',
  color: '#eef6ff',
  fontFamily: 'Sora, -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem',
};

function Splash({ msg }) {
  return (
    <div style={baseStyle}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '0.75rem', color: '#45688a', letterSpacing: '0.16em', textTransform: 'uppercase' }}>
          Deskte
        </div>
        <div style={{ marginTop: '1rem', color: '#a5b4fc' }}>{msg}</div>
      </div>
    </div>
  );
}

function SignInScreen({ onLogin }) {
  return (
    <div style={baseStyle}>
      <div style={{ maxWidth: 420, width: '100%', textAlign: 'center' }}>
        <img
          src="/logo/deskte-ocean.svg"
          alt="Deskte"
          style={{ height: 96, maxWidth: '85%', width: 'auto', display: 'block', margin: '0 auto 0.5rem' }}
        />
        <div style={{ fontSize: '0.64rem', color: '#45688a', letterSpacing: '0.16em', marginBottom: '2rem' }}>
          YOUR INSURANCE DESK
        </div>
        <div
          style={{
            background: '#112538',
            border: '1px solid #1a3550',
            borderRadius: 12,
            padding: '1.5rem',
          }}
        >
          <div style={{ fontSize: '0.85rem', color: '#a5b4fc', marginBottom: '1rem' }}>
            Sign in to access your CRM
          </div>
          <button
            onClick={onLogin}
            style={{
              width: '100%',
              padding: '0.75rem',
              borderRadius: 10,
              background: 'linear-gradient(135deg,#0099cc,#0055aa)',
              border: 'none',
              color: '#fff',
              fontSize: '0.9rem',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(0,153,204,.3)',
            }}
          >
            🔐 Sign in with Microsoft
          </button>
          <div style={{ fontSize: '0.65rem', color: '#45688a', marginTop: '1rem' }}>
            Powered by Microsoft Entra External ID
          </div>
        </div>
      </div>
    </div>
  );
}

// Plans must mirror the source-of-truth in
// reference_pricing_plan.md (approved 2026-04-21) +
// decision_seat_naming.md (2026-05-04 "Asistente" → "Seat" rename).
// Solo / Agency do NOT include any seat/assistant — extras are an
// add-on ($9.99/mo) sold separately. Enterprise is contact-sales
// (no fixed price, no "unlimited" wording).
const REGISTRATION_PLANS = [
  {
    id: 'SOLO',
    name: 'Solo',
    price: '$45',
    blurb: 'Best for individual agents starting out',
    blurbEs: 'Ideal para agentes que empiezan solos',
    // 1,000, not 350: 350 is only the Agency.maxClients column default, which
    // /auth/register overwrites with the real Solo cap (raised 500 -> 1,000 on
    // 2026-08-13). Keep this list, the pricing site and auth.routes.ts in sync —
    // this screen was already the odd one out once and under-sold the plan.
    perks: [
      '1,000 clients',
      '1 agent',
      '500 e-signatures / year',
      '500 doc requests / year',
    ],
    perksEs: [
      '1,000 clientes',
      '1 agente',
      '500 firmas / año',
      '500 solicitudes de docs / año',
    ],
  },
  {
    id: 'AGENCY',
    name: 'Agency',
    price: '$200',
    blurb: 'For growing agencies with a team',
    blurbEs: 'Para agencias en crecimiento con equipo',
    perks: [
      '1,500 clients (shared pool)',
      '3 agents',
      '1,000 e-signatures / year',
      'Custom branding',
    ],
    perksEs: [
      '1,500 clientes (compartido)',
      '3 agentes',
      '1,000 firmas / año',
      'Branding personalizado',
    ],
    recommended: true,
  },
  {
    id: 'ENTERPRISE',
    name: 'Enterprise',
    price: 'Contact',
    priceEs: 'Consulta',
    blurb: 'Larger agencies & multi-office teams',
    blurbEs: 'Agencias grandes y multi-oficina',
    perks: [
      'Custom limits',
      'API access',
      'Dedicated onboarding',
      'Custom BAA',
      'Priority support',
    ],
    perksEs: [
      'Límites a la medida',
      'Acceso API',
      'Onboarding dedicado',
      'BAA personalizado',
      'Soporte prioritario',
    ],
  },
];

function NeedsRegistrationScreen({ onLogout, email, displayName, onRegistered }) {
  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem('deskte-lang');
    if (saved === 'es' || saved === 'en') return saved;
    // Default to English for first-time visitors (per owner decision
    // 2026-07-07). The signup screen shows an explicit English/Español
    // selector, and whatever is chosen here becomes the user's
    // languagePreference — which drives the language of every automated
    // email (welcome, tickets, trial). No choice → English.
    return 'en';
  });
  const es = lang === 'es';
  const setLangPersist = (next) => {
    setLang(next);
    try { localStorage.setItem('deskte-lang', next); } catch (_) {}
  };

  const defaultAgencyName = displayName
    ? `${displayName.split(/\s+/)[0]}${es ? "'s Agency" : "'s Agency"}`
    : '';
  const [agencyName, setAgencyName] = useState(defaultAgencyName);
  const [plan, setPlan] = useState('AGENCY');
  const [acceptTerms, setAcceptTerms] = useState(false);
  // Pre-fill from URL ?invite=XXXX so Lissette can hand out a single link
  // that lands the agent on the form with the code already typed.
  const [inviteCode, setInviteCode] = useState(() => {
    try {
      const u = new URL(window.location.href);
      return (u.searchParams.get('invite') || '').toUpperCase();
    } catch (_) { return ''; }
  });
  // Programa de referidos (2026-08-16): ?ref=CODE es el enlace que comparte un
  // agente. No se muestra ningún campo — quien llega por ahí no tiene que hacer
  // nada, el código viaja con el registro y liga las dos cuentas.
  //
  // Se lee UNA vez, al montar. MSAL reescribe la URL al volver del login y el
  // parámetro puede desaparecer del address bar; leerlo en cada render lo
  // perdería justo cuando hace falta, al enviar el formulario.
  const [referralCode] = useState(() => {
    try {
      const u = new URL(window.location.href);
      return (u.searchParams.get('ref') || '').trim().toUpperCase();
    } catch (_) { return ''; }
  });
  // Public launch (2026-08-09): PRIVATE_BETA is off, so /auth/register accepts
  // anyone and the code field is no longer part of the normal signup path.
  // It still renders (pre-filled) when the URL carries ?invite=CODE, so a
  // coded link keeps working if the gate is ever switched back on — the
  // minting UI in Platform Admin is untouched.
  //
  // Derived ONCE from the initial URL rather than from `inviteCode` directly:
  // gating on the live value would make the field vanish the moment someone
  // clears the input to retype it.
  const [showInviteField] = useState(() => inviteCode.length > 0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // Cuenta creada pero Checkout no arrancó. Guarda POR QUÉ, para poder decirlo
  // y reintentar sin volver a registrar (el registro ya no se puede repetir:
  // el segundo intento contesta 409 "User already registered").
  const [checkoutIssue, setCheckoutIssue] = useState(null);

  const canSubmit = agencyName.trim().length >= 2 && acceptTerms && !busy;

  // Lleva a Stripe Checkout, o lanza con un `code` que diga qué falló.
  // `available:false` significa que ese plan no tiene precio configurado en
  // este despliegue — no es un fallo de red y no se arregla reintentando.
  const startCheckout = async () => {
    const products = await listBillingProducts();
    const chosen = (products?.plans || []).find((p) => p.id === plan);
    if (!chosen?.available || !chosen.priceId) {
      const e = new Error('Plan not sellable');
      e.code = 'PLAN_NOT_SELLABLE';
      throw e;
    }
    const { url } = await createBillingCheckout(chosen.priceId);
    if (!url) {
      const e = new Error('No checkout URL');
      e.code = 'NO_CHECKOUT_URL';
      throw e;
    }
    window.location.assign(url);
  };

  const retryCheckout = async () => {
    setBusy(true);
    setErr(null);
    try {
      await startCheckout();
    } catch (e2) {
      setCheckoutIssue(e2?.code || 'CHECKOUT_FAILED');
      setBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setErr(null);
    try {
      await registerAccount({
        agencyName: agencyName.trim(),
        plan,
        language: lang,
        inviteCode: inviteCode.trim() || undefined,
        referralCode: referralCode || undefined,
      });

      // Card up front (policy since 2026-08-09, ToS §6.1). Send the brand-new
      // agency straight to Stripe Checkout for the plan they picked: Checkout
      // stores the card, charges $0 today, and opens the 7-day trial INSIDE
      // Stripe, which then bills automatically on day 7. That subscription is
      // the whole enforcement mechanism — nothing in the app blocks a lapsed
      // trial, by design.
      //
      // ENTERPRISE is quote-only (no self-serve price), so it skips this and
      // gets in — it is sold by hand.
      //
      // Everything else MUST reach Checkout. Until 2026-08-16 a failure here
      // was swallowed and the browser fell through into the CRM, so a missing
      // STRIPE_PRICE_* in the Container App handed out free accounts with
      // nothing on any screen saying so ("la gente se inscribe y ya no le pide
      // tarjeta ni nada"). Now the failure is shown: the account exists — it
      // was created a line above and we are NOT going to delete it — but the
      // visitor lands on a screen that says a payment method is missing and
      // offers to retry. Getting in without a card stays possible on purpose
      // (a broken Stripe must not lock a real customer out of a product they
      // just signed up for), except it is now a deliberate click instead of a
      // silent default, and Platform Admin counts those accounts.
      if (plan === 'ENTERPRISE') {
        onRegistered();
        return;
      }
      try {
        await startCheckout();
        return; // leave `busy` set — we're navigating away to Stripe
      } catch (checkoutErr) {
        setCheckoutIssue(checkoutErr?.code || 'CHECKOUT_FAILED');
        setBusy(false);
        return;
      }
    } catch (e2) {
      // INVITE_CODE_REQUIRED / INVITE_CODE_INVALID / INVITE_CODE_EXPIRED /
      // INVITE_CODE_EXHAUSTED all come back from the backend with an .code
      // field. Translate to a friendlier message in the chosen language.
      const code = e2.code || '';
      let msg = e2.message;
      if (es) {
        if (code === 'INVITE_CODE_REQUIRED') msg = 'Deskte está en beta privada. Pide tu código en hello@deskte.com.';
        else if (code === 'INVITE_CODE_INVALID') msg = 'Código de invitación no válido. Revisa los caracteres.';
        else if (code === 'INVITE_CODE_EXPIRED') msg = 'Este código de invitación ya expiró.';
        else if (code === 'INVITE_CODE_EXHAUSTED') msg = 'Este código ya fue usado al máximo.';
      } else {
        if (code === 'INVITE_CODE_REQUIRED') msg = 'Deskte is in private beta. Request a code at hello@deskte.com.';
        else if (code === 'INVITE_CODE_INVALID') msg = 'Invite code is not valid. Double-check the characters.';
        else if (code === 'INVITE_CODE_EXPIRED') msg = 'This invite code has expired.';
        else if (code === 'INVITE_CODE_EXHAUSTED') msg = 'This invite code has been used up.';
      }
      setErr(msg || (es ? 'Error al crear cuenta' : 'Registration failed'));
      setBusy(false);
    }
  };

  // La cuenta se creó pero no se llegó a Stripe. Antes esto entraba al CRM sin
  // decir nada; ahora se ve, se puede reintentar, y entrar sin tarjeta es un
  // clic aparte y explícito.
  if (checkoutIssue) {
    const notSellable = checkoutIssue === 'PLAN_NOT_SELLABLE';
    return (
      <div style={baseStyle}>
        <div style={{ maxWidth: 460, width: '100%', textAlign: 'center' }}>
          <img
            src="/logo/deskte-ocean.svg"
            alt="Deskte"
            style={{ height: 56, maxWidth: '60%', width: 'auto', display: 'block', margin: '0 auto 1.25rem' }}
          />
          <h2 style={{ color: '#eef6ff', fontSize: '1.1rem', margin: '0 0 .75rem' }}>
            {es ? 'Falta tu método de pago' : 'Payment method missing'}
          </h2>
          <p style={{ color: '#7aa3c0', fontSize: '.82rem', lineHeight: 1.6, margin: '0 0 1.25rem' }}>
            {es ? (
              <>
                Tu cuenta <strong style={{ color: '#eef6ff' }}>{email}</strong> ya está creada, pero no pudimos
                abrir la pantalla de pago{notSellable ? ' porque ese plan no está disponible para cobro en este momento' : ''}.
                {' '}No se te ha cobrado nada.
              </>
            ) : (
              <>
                Your account <strong style={{ color: '#eef6ff' }}>{email}</strong> is created, but we couldn't open
                the payment screen{notSellable ? " because that plan isn't available for checkout right now" : ''}.
                {' '}You have not been charged.
              </>
            )}
          </p>
          <button
            type="button"
            onClick={retryCheckout}
            disabled={busy}
            style={{
              width: '100%', padding: '.75rem', borderRadius: 10, border: 'none',
              background: busy ? '#162d44' : '#00d4ff', color: busy ? '#45688a' : '#0a1628',
              fontWeight: 800, fontSize: '.85rem', fontFamily: 'inherit',
              cursor: busy ? 'wait' : 'pointer',
            }}
          >
            {busy ? (es ? 'Abriendo...' : 'Opening...') : (es ? 'Agregar tarjeta' : 'Add card')}
          </button>
          <button
            type="button"
            onClick={onRegistered}
            style={{
              marginTop: '.75rem', background: 'none', border: 'none', color: '#7aa3c0',
              fontSize: '.75rem', fontFamily: 'inherit', cursor: 'pointer', textDecoration: 'underline',
            }}
          >
            {es ? 'Entrar y agregar la tarjeta después' : 'Continue and add the card later'}
          </button>
          <div style={{ marginTop: '1rem', fontSize: '.68rem', color: '#45688a', lineHeight: 1.5 }}>
            {es
              ? '¿Sigue sin funcionar? Escríbenos a hello@deskte.com y lo resolvemos.'
              : "Still not working? Email hello@deskte.com and we'll sort it out."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...baseStyle, alignItems: 'flex-start', paddingTop: '3rem', paddingBottom: '3rem' }}>
      <div style={{ maxWidth: 720, width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <img
            src="/logo/deskte-ocean.svg"
            alt="Deskte"
            style={{ height: 64, maxWidth: '70%', width: 'auto', display: 'block', margin: '0 auto 0.25rem' }}
          />
          <div style={{ fontSize: '0.6rem', color: '#45688a', letterSpacing: '0.16em' }}>
            YOUR INSURANCE DESK
          </div>
          {/* Explicit language selector. Whatever is picked here is sent as
              `language` on register and stored as the user's languagePreference,
              which drives the language of every automated email. */}
          <div style={{ marginTop: '0.75rem', display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem' }}>
            <div style={{ display: 'inline-flex', border: '1px solid #1a3550', borderRadius: 8, overflow: 'hidden' }}>
              {[
                { code: 'en', label: 'English' },
                { code: 'es', label: 'Español' },
              ].map((opt) => {
                const active = lang === opt.code;
                return (
                  <button
                    key={opt.code}
                    type="button"
                    onClick={() => setLangPersist(opt.code)}
                    aria-pressed={active}
                    style={{
                      background: active ? '#00a8c8' : 'transparent',
                      color: active ? '#06283d' : '#7aa3c0',
                      fontWeight: active ? 700 : 500,
                      border: 'none',
                      fontSize: '0.72rem',
                      padding: '0.35rem 0.9rem',
                      cursor: 'pointer',
                      letterSpacing: '0.02em',
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <div style={{ fontSize: '0.6rem', color: '#5b7a9a', letterSpacing: '0.02em' }}>
              {es ? 'Recibirás tus correos en este idioma' : "You'll receive your emails in this language"}
            </div>
          </div>
        </div>

        <div style={{
          background: '#112538',
          border: '1px solid #1a3550',
          borderRadius: 14,
          padding: '2rem 1.75rem',
        }}>
          <div style={{ fontSize: '1.75rem', textAlign: 'center', marginBottom: '0.25rem' }}>👋</div>
          <h2 style={{
            color: '#eef6ff',
            fontFamily: 'Sora, sans-serif',
            fontWeight: 700,
            fontSize: '1.15rem',
            textAlign: 'center',
            margin: '0 0 0.4rem',
          }}>
            {es ? 'Bienvenido a Deskte' : 'Welcome to Deskte'}
          </h2>
          <p style={{
            color: '#a5b4fc',
            fontSize: '0.82rem',
            lineHeight: 1.55,
            textAlign: 'center',
            margin: '0 0 1.5rem',
          }}>
            {es
              ? <>Tu cuenta <strong style={{ color: '#eef6ff' }}>{email}</strong> está autenticada con Microsoft. Empieza tu prueba gratis de 7 días.</>
              : <>Your account <strong style={{ color: '#eef6ff' }}>{email}</strong> is authenticated with Microsoft. Start your 7-day free trial.</>}
          </p>

          <form onSubmit={handleSubmit}>
            <label style={{
              display: 'block',
              fontSize: '0.72rem',
              color: '#7aa3c0',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              marginBottom: '0.4rem',
            }}>
              {es ? 'Nombre de tu agencia o agente' : 'Your agency or agent name'}
            </label>
            <input
              type="text"
              value={agencyName}
              onChange={(e) => setAgencyName(e.target.value)}
              maxLength={120}
              required
              placeholder={es ? 'Ej. Maria Lopez o Maria Insurance Group' : 'e.g. Maria Lopez or Maria Insurance Group'}
              style={{
                width: '100%',
                background: '#0b1622',
                border: '1px solid #1a3550',
                borderRadius: 10,
                padding: '0.7rem 0.85rem',
                color: '#eef6ff',
                fontSize: '0.92rem',
                outline: 'none',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => { e.target.style.borderColor = '#00d4ff'; }}
              onBlur={(e) => { e.target.style.borderColor = '#1a3550'; }}
            />

            {showInviteField && (
              <>
                <label style={{
                  display: 'block',
                  fontSize: '0.72rem',
                  color: '#7aa3c0',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  margin: '1.25rem 0 0.4rem',
                }}>
                  {es ? 'Código de invitación' : 'Invite code'}
                </label>
                <input
                  type="text"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  maxLength={32}
                  autoComplete="off"
                  placeholder="ABCD-EFGH-JKMN"
                  style={{
                    width: '100%',
                    background: '#0b1622',
                    border: '1px solid #1a3550',
                    borderRadius: 10,
                    padding: '0.7rem 0.85rem',
                    color: '#eef6ff',
                    fontSize: '0.92rem',
                    letterSpacing: '0.12em',
                    fontFamily: 'JetBrains Mono, Consolas, monospace',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = '#00d4ff'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#1a3550'; }}
                />
                <div style={{ fontSize: '0.65rem', color: '#45688a', marginTop: '0.35rem', lineHeight: 1.45 }}>
                  {es
                    ? 'Tu enlace ya trae el código de invitación. Déjalo tal cual.'
                    : 'Your link already carries the invite code. Leave it as is.'}
                </div>
              </>
            )}

            <div style={{
              fontSize: '0.72rem',
              color: '#7aa3c0',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              margin: '1.25rem 0 0.5rem',
            }}>
              {es ? 'Elige tu plan' : 'Pick your plan'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.5rem' }}>
              {REGISTRATION_PLANS.map((p) => {
                const selected = plan === p.id;
                return (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => setPlan(p.id)}
                    style={{
                      textAlign: 'left',
                      cursor: 'pointer',
                      position: 'relative',
                      background: selected ? 'rgba(0, 212, 255, 0.08)' : '#0b1622',
                      border: selected ? '1px solid #00d4ff' : '1px solid #1a3550',
                      borderRadius: 10,
                      padding: '0.85rem 0.95rem',
                      color: '#eef6ff',
                      fontFamily: 'inherit',
                    }}
                  >
                    {p.recommended && (
                      <span style={{
                        position: 'absolute',
                        top: -8,
                        right: 10,
                        background: '#00d4ff',
                        color: '#070d14',
                        fontSize: '0.55rem',
                        fontWeight: 800,
                        padding: '2px 7px',
                        borderRadius: 6,
                        letterSpacing: '0.08em',
                      }}>
                        {es ? 'POPULAR' : 'POPULAR'}
                      </span>
                    )}
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                      <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{p.name}</div>
                      <div style={{ fontSize: '0.78rem', color: '#7aa3c0' }}>
                        {p.id === 'ENTERPRISE'
                          ? (es ? (p.priceEs || 'Consulta') : (p.price || 'Contact'))
                          : <>{p.price}<span style={{ fontSize: '0.62rem' }}>/mo</span></>}
                      </div>
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#7aa3c0', marginBottom: '0.5rem', lineHeight: 1.4 }}>
                      {es ? p.blurbEs : p.blurb}
                    </div>
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: '0.7rem', color: '#a5b4fc' }}>
                      {(es ? p.perksEs : p.perks).map((perk) => (
                        <li key={perk} style={{ display: 'flex', gap: 4, marginBottom: 2 }}>
                          <span style={{ color: '#00d4ff' }}>✓</span> {perk}
                        </li>
                      ))}
                    </ul>
                  </button>
                );
              })}
            </div>

            <div style={{
              fontSize: '0.65rem',
              color: '#45688a',
              marginTop: '0.5rem',
              textAlign: 'center',
            }}>
              {/* Decía "sin tarjeta de crédito" y contradecía la política de
                  tarjeta por adelantado (ToS §6.1) — y a la propia pantalla,
                  que lleva a Stripe Checkout al terminar. */}
              {es
                ? '7 días gratis. Pedimos tarjeta, pero hoy no se cobra nada.'
                : '7 days free. A card is required, but nothing is charged today.'}
            </div>

            {/* El consentimiento, en un recuadro que se ve.
                Lissette, 2026-08-20: "esta saliendo ahi en un cuadrito
                chiquitico". Estaba a 0.75rem en lavanda (#a5b4fc) sobre
                oscuro, sin borde ni fondo, debajo del selector de plan.
                Se leia como un pie de pagina.
                No es solo estetica: un clickwrap vale cuando las condiciones
                se presentan de forma NOTORIA y el consentimiento es
                inequivoco. Doce pixeles de bajo contraste debajo de un precio
                es justo lo que se discute despues — y aqui se acepta ademas
                el aviso sobre PHI, que es la linea que mas pesa. */}
            <label style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.6rem',
              marginTop: '1.25rem',
              padding: '0.85rem 0.9rem',
              background: 'rgba(0,212,255,.05)',
              border: '1px solid rgba(0,212,255,.28)',
              borderRadius: 10,
              fontSize: '0.82rem',
              color: '#dbe6f5',
              lineHeight: 1.6,
              cursor: 'pointer',
            }}>
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                style={{ marginTop: 3, width: 17, height: 17, flexShrink: 0, cursor: 'pointer', accentColor: '#00d4ff' }}
              />
              <span>
                {es ? 'Acepto los ' : 'I agree to the '}
                <a href="https://deskte.com/legal/terms-of-service/" target="_blank" rel="noopener noreferrer" style={{ color: '#00d4ff' }}>
                  {es ? 'Términos de Servicio' : 'Terms of Service'}
                </a>
                {es ? ', la ' : ', the '}
                <a href="https://deskte.com/legal/privacy-policy/" target="_blank" rel="noopener noreferrer" style={{ color: '#00d4ff' }}>
                  {es ? 'Política de Privacidad' : 'Privacy Policy'}
                </a>
                {es ? ' y la ' : ' and the '}
                <a href="https://deskte.com/legal/acceptable-use/" target="_blank" rel="noopener noreferrer" style={{ color: '#00d4ff' }}>
                  {es ? 'Política de Uso Aceptable' : 'Acceptable Use Policy'}
                </a>
                {es ? '.' : '.'}
                {/* Esta frase decia "entiendo que DEBO FIRMAR un BAA antes de
                    procesar datos reales" — algo que el propio interesado
                    tenia que ir a hacer por su cuenta, sin decir donde. Desde
                    2026-08-20 el BAA se pide dentro de Deskte al dueno de la
                    agencia (ver legalDocs.ts#baaClickwrapOn), asi que dejarla
                    como estaba seria mandar a alguien a buscar un tramite que
                    el sistema ya le va a poner delante. */}
                <span style={{ display: 'block', marginTop: '.5rem', color: '#a5b4fc', fontSize: '.78rem' }}>
                  {es
                    ? 'Si vas a manejar información médica protegida (PHI), Deskte te pedirá aceptar el Acuerdo de Asociado de Negocios (BAA) al entrar, antes de trabajar con datos reales.'
                    : "If you'll handle Protected Health Information (PHI), Deskte will ask you to accept the Business Associate Agreement (BAA) when you sign in, before working with real data."}
                </span>
              </span>
            </label>

            {err && (
              <div style={{
                background: 'rgba(248, 113, 113, 0.1)',
                border: '1px solid rgba(248, 113, 113, 0.4)',
                color: '#f87171',
                padding: '0.6rem 0.75rem',
                borderRadius: 8,
                fontSize: '0.78rem',
                marginTop: '1rem',
              }}>
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              style={{
                width: '100%',
                marginTop: '1.25rem',
                padding: '0.85rem',
                borderRadius: 10,
                background: canSubmit ? 'linear-gradient(135deg,#00d4ff,#00b4d8)' : '#2a4560',
                border: 'none',
                color: canSubmit ? '#070d14' : '#7aa3c0',
                fontSize: '0.92rem',
                fontWeight: 800,
                fontFamily: 'Oxanium, Sora, sans-serif',
                cursor: canSubmit ? 'pointer' : 'not-allowed',
                letterSpacing: '0.02em',
              }}
            >
              {busy
                ? (es ? 'Creando tu agencia…' : 'Creating your agency…')
                : (es ? 'Crear mi agencia →' : 'Create my agency →')}
            </button>
          </form>

          <div style={{
            textAlign: 'center',
            marginTop: '1rem',
            fontSize: '0.7rem',
            color: '#45688a',
          }}>
            {es ? '¿Es la cuenta equivocada? ' : 'Wrong account? '}
            <button
              type="button"
              onClick={onLogout}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#7aa3c0',
                cursor: 'pointer',
                padding: 0,
                fontSize: 'inherit',
                textDecoration: 'underline',
              }}
            >
              {es ? 'Cerrar sesión' : 'Sign out'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// LO QUE VE QUIEN NO PUEDE ACEPTAR (E-309, 2026-09-21).
//
// Esta pantalla está entera en español y hasta ahora enseñaba el texto en
// inglés que devuelve la API. Los dos motivos nuevos son justo los que le
// tocan a una dueña que se invitó a sí misma, así que son los que más
// importa que se entiendan a la primera — y que digan QUÉ HACER en vez de
// dejarla mirando un error.
const MOTIVOS_NO_ACEPTAR = {
  OWNER_CANNOT_ACCEPT:
    'Tu cuenta es la DUEÑA de una agencia en Deskte. Aceptar esta invitación te convertiría en un miembro más de un equipo y perderías el mando de tu propia cuenta, así que no se aplica. Si querías darle acceso a otra persona, invítala con EL CORREO DE ELLA. Tu cuenta sigue intacta: entra normal por app.deskte.com.',
  WOULD_DOWNGRADE:
    'Ya eres agente en esta agencia. Aceptar una invitación de asiento te bajaría el acceso y te dejaría sin tu cartera, así que no se aplica. Si el cambio es a propósito, que la dueña te lo haga desde Equipo.',
};

function JoinScreen({ token, isAuthed, onLogin, onLogout, accountEmail, onAccepted, onCancel }) {
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  // Lookup metadata so we can show "You're invited as X" before sign-in
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await lookupInvitation(token);
        if (!cancelled) setMeta(data);
      } catch (e) {
        if (!cancelled) setErr(e.message || 'Invitation not found or expired');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleSignIn = () => {
    sessionStorage.setItem('deskte:pendingInviteToken', token);
    onLogin();
  };

  const handleAccept = async () => {
    setBusy(true);
    setErr(null);
    try {
      await acceptInvitation(token);
      onAccepted();
    } catch (e) {
      setErr(MOTIVOS_NO_ACEPTAR[e.code] || e.message || 'Could not accept invitation');
      setBusy(false);
    }
  };

  if (err) {
    return (
      <div style={baseStyle}>
        <div style={{ maxWidth: 460, textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⚠️</div>
          <h2 style={{ color: '#f87171', fontWeight: 600 }}>Invitación no válida</h2>
          <p style={{ color: '#cbd5e1', lineHeight: 1.6, fontSize: '0.85rem' }}>{err}</p>
          <button onClick={onCancel} style={{ marginTop: '1rem', padding: '0.6rem 1.2rem', borderRadius: 8, background: 'transparent', border: '1px solid #1a3550', color: '#a5b4fc', cursor: 'pointer' }}>Volver al inicio</button>
        </div>
      </div>
    );
  }

  if (!meta) return <Splash msg="Verificando invitación…" />;

  return (
    <div style={baseStyle}>
      <div style={{ maxWidth: 460, width: '100%', textAlign: 'center' }}>
        <img src="/logo/deskte-ocean.svg" alt="Deskte" style={{ height: 80, maxWidth: '70%', width: 'auto', display: 'block', margin: '0 auto 0.5rem' }} />
        <div style={{ fontSize: '0.62rem', color: '#45688a', letterSpacing: '0.16em', marginBottom: '1.5rem' }}>YOUR INSURANCE DESK</div>

        <div style={{ background: '#112538', border: '1px solid #1a3550', borderRadius: 14, padding: '1.5rem' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✉️</div>
          <h2 style={{ color: '#eef6ff', fontWeight: 700, fontSize: '1.05rem', margin: '0 0 0.4rem' }}>Has sido invitado</h2>
          <p style={{ color: '#a5b4fc', fontSize: '0.85rem', lineHeight: 1.6, margin: '0 0 1rem' }}>
            Únete a <strong style={{ color: '#00d4ff' }}>{meta.agencyName}</strong>.
          </p>
          <div style={{ background: '#0d1c2e', border: '1px solid #1a3550', borderRadius: 9, padding: '0.6rem 0.8rem', marginBottom: '1rem', fontSize: '0.78rem', color: '#7aa3c0' }}>
            Email: <strong style={{ color: '#eef6ff' }}>{meta.email}</strong>
          </div>

          {!isAuthed ? (
            <>
              <button onClick={handleSignIn} style={{ width: '100%', padding: '0.7rem', borderRadius: 10, background: 'linear-gradient(135deg,#0099cc,#0055aa)', border: 'none', color: '#fff', fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer' }}>
                🔐 Iniciar sesión y aceptar
              </button>
              <p style={{ fontSize: '0.65rem', color: '#45688a', marginTop: '0.85rem', lineHeight: 1.5 }}>
                Inicia sesión con <strong>{meta.email}</strong>. Si aún no tienes cuenta, podrás registrarte en el mismo flujo.
              </p>
            </>
          ) : (
            <>
              {accountEmail && accountEmail.toLowerCase() !== meta.email.toLowerCase() && (
                <div style={{ background: 'rgba(255,183,0,.08)', border: '1px solid rgba(255,183,0,.3)', borderRadius: 8, padding: '0.5rem 0.65rem', marginBottom: '0.75rem', fontSize: '0.7rem', color: '#ffb700', textAlign: 'left' }}>
                  ⚠️ Estás conectado como <strong>{accountEmail}</strong> pero la invitación es para <strong>{meta.email}</strong>. Cambia de cuenta para aceptar.
                </div>
              )}
              <button onClick={handleAccept} disabled={busy || (accountEmail && accountEmail.toLowerCase() !== meta.email.toLowerCase())} style={{ width: '100%', padding: '0.7rem', borderRadius: 10, background: busy ? '#2a4560' : 'linear-gradient(135deg,#059669,#10b981)', border: 'none', color: '#fff', fontSize: '0.88rem', fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer' }}>
                {busy ? 'Aceptando…' : '✅ Aceptar invitación'}
              </button>
              <button onClick={onLogout} style={{ marginTop: '0.5rem', width: '100%', padding: '0.5rem', borderRadius: 8, background: 'transparent', border: '1px solid #1a3550', color: '#a5b4fc', fontSize: '0.75rem', cursor: 'pointer' }}>
                Cerrar sesión y entrar con otra cuenta
              </button>
            </>
          )}
        </div>

        <button onClick={onCancel} style={{ marginTop: '1rem', background: 'transparent', border: 'none', color: '#45688a', fontSize: '0.7rem', cursor: 'pointer' }}>
          Cancelar
        </button>
      </div>
    </div>
  );
}

function ErrorScreen({ err, onLogout, onRetry }) {
  return (
    <div style={baseStyle}>
      <div style={{ maxWidth: 520 }}>
        <h3 style={{ color: '#f87171' }}>No pudimos cargar tu perfil</h3>
        <pre
          style={{
            background: '#050505',
            padding: '1rem',
            borderRadius: 8,
            color: '#fca5a5',
            fontSize: '0.75rem',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}
        >
          {JSON.stringify(err, null, 2)}
        </pre>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
          <button
            onClick={onRetry}
            style={{ padding: '0.5rem 1rem', borderRadius: 8, border: '1px solid #1a3550', background: '#112538', color: '#a5b4fc', cursor: 'pointer' }}
          >
            Reintentar
          </button>
          <button
            onClick={onLogout}
            style={{ padding: '0.5rem 1rem', borderRadius: 8, border: '1px solid #1a3550', background: 'transparent', color: '#a5b4fc', cursor: 'pointer' }}
          >
            Cerrar sesión
          </button>
        </div>
      </div>
    </div>
  );
}
